<template>
  <div class="bill-manage-page">
    <!-- 页面主体布局 -->
    <div class="page-container">
      <!-- 移除左侧实体树区域 -->
      <div class="left-sidebar" style="display: none;">
        <!-- 筛选条件 -->
        <div class="filter-section">
          <div class="filter-row">
            <label class="filter-label">账单类型:</label>
            <el-select
              filterable
              v-model.trim="formInline.billType"
              placeholder="请选择账单类型"
              @change="handleBillTypeChange"
              class="filter-select"
            >
              <el-option
                v-for="item in billTypeList"
                :key="item.value"
                :label="item.name"
                :value="item.value"
              >
              </el-option>
            </el-select>
          </div>

          <div class="filter-row">
            <label class="filter-label">选择销售:</label>
            <el-select
              clearable
              filterable
              v-model.trim="formInline.sales"
              placeholder=""
              @change="handleSalesChange"
              class="filter-select"
            >
              <el-option
                v-for="item in filteredSalesList"
                :key="item.id"
                :label="item.name"
                :value="item.name"
              >
              </el-option>
            </el-select>
          </div>

          <div class="filter-row">
            <label class="filter-label">选择伙伴:</label>
            <el-select
              v-model="formInline.selected_agent_record_id"
              placeholder=""
              class="filter-select"
              clearable
              filterable
              @change="handleAgentChange"
              :popper-append-to-body="false"
            >
              <el-option
                v-for="item in agentsListWithRecordId"
                :key="item.recordId"
                :label="`${item.company} (${item.agent_id})`"
                :value="item.recordId"
              >
              </el-option>
            </el-select>
          </div>

          <div class="filter-row">
            <label class="filter-label">选择payer:</label>
            <el-select
              v-model="formInline.payer_id"
              placeholder=""
              class="filter-select"
              clearable
              filterable
              @change="handlePayerChange"
            >
              <el-option
                v-for="item in payersList"
                :key="item.payer_id"
                :label="item.name + '（' + item.payer_id + '）'"
                :value="item.payer_id"
              >
              </el-option>
            </el-select>
          </div>

          <div class="filter-row">
            <label class="filter-label">选择link:</label>
            <el-select
              v-model="formInline.link_id"
              placeholder=""
              class="filter-select"
              clearable
              filterable
              :filter-method="linkFilterMethod"
              no-data-text="请输入内容进行搜索"
              no-match-text="无匹配数据"
              @change="handleLinkChange"
              @focus="handleLinkFocus"
              @blur="handleLinkBlur"
            >
              <el-option
                v-for="item in displayedLinksList"
                :key="item.link_id"
                :label="(item.customer_name || item.name || `客户${item.link_id}`) + '（linkID：' + item.link_id + '）'"
                :value="item.link_id"
                :title="(item.customer_name || item.name || `客户${item.link_id}`) + '（linkID：' + item.link_id + '）'"
              >
              </el-option>
            </el-select>
          </div>
        </div>

        <!-- 实体树 -->
        <div class="entity-tree-section">
          <div class="tree-tabs">
            <div class="tab-item active">EA实体树</div>
          </div>

          <div class="tree-container">
            <el-scrollbar height="calc(100vh - 260px)">
              <div v-if="loading" style="padding: 14px; text-align: center; color: #999;">
                正在加载数据...
              </div>
              <el-tree
                v-show="!loading"
                ref="entityTree"
                :data="entityTreeData"
                :props="treeProps"
                node-key="id"
                :expand-on-click-node="false"
                :default-expanded-keys="defaultExpandedKeys"
                :highlight-current="true"
                @node-click="handleTreeNodeClick"
                :load="loadNode"
                class="entity-tree"
                :lazy="isLazyMode"
                :indent="16"
              >
                <template #default="{ data }">
                  <span class="tree-node">
                    <el-icon
                      v-if="data.type === 'agent' || data.type === 'payer'"
                      class="folder-icon"
                    >
                      <FolderOpened />
                    </el-icon>
                    <el-icon v-else-if="data.type === 'link'" class="document-icon">
                      <Document />
                    </el-icon>
                    <el-icon v-else class="document-icon">
                      <Document />
                    </el-icon>
                    <span class="node-label">{{ data.label }}</span>
                    <span v-if="data.region" class="region-tag">{{
                      data.region
                    }}</span>
                  </span>
                </template>
              </el-tree>
            </el-scrollbar>
          </div>
        </div>
      </div>

      <!-- 右侧内容区域（全宽） -->
      <div class="right-content" style="width: 100%;">
        <!-- 顶部Tabs -->
        <div class="content-tabs">
          <!-- 自定义Tabs布局 -->
          <div class="custom-tabs">
            <!-- Tab导航 -->
            <div class="tab-nav">
              <div
                class="tab-item"
                :class="{ active: activeTab === 'cur' }"
                @click="activeTab = 'cur'"
              >
                <div class="tab-icon">
                  <el-icon><Document /></el-icon>
                </div>
                <div class="tab-label">CUR版账单</div>
                <div class="tab-indicator"></div>
              </div>

              <div
                v-if="showAllTabs"
                class="tab-item"
                :class="{ active: activeTab === 'settings' }"
                @click="activeTab = 'settings'"
              >
                <div class="tab-icon">
                  <el-icon><Upload /></el-icon>
                </div>
                <div class="tab-label">Invoice上传与配置</div>
                <div class="tab-indicator"></div>
              </div>

              <div
                v-if="showAllTabs"
                class="tab-item"
                :class="{ active: activeTab === 'integration' }"
                @click="activeTab = 'integration'"
              >
                <div class="tab-icon">
                  <el-icon><Grid /></el-icon>
                </div>
                <div class="tab-label">整合表校验与批量下载</div>
                <div class="tab-indicator"></div>
              </div>

              <div
                v-if="showRebateTab"
                class="tab-item"
                :class="{ active: activeTab === 'rebate' }"
                @click="handleRebateTabClick"
              >
                <div class="tab-icon">
                  <el-icon><CircleCheck /></el-icon>
                </div>
                <div class="tab-label">返点核对</div>
                <div class="tab-indicator"></div>
              </div>
            </div>

            <!-- Tab内容 -->
            <div class="tab-content">
              <!-- CUR版账单内容：默认tab，保持v-show避免重复加载 -->
              <div v-show="activeTab === 'cur'" class="cur-bill-content">
                <CurBillManage />
              </div>

              <!-- 返点核对内容 - 固定页面，无滚动条 -->
              <div v-if="activeTab === 'rebate'" class="rebate-content">
                <CheckRebate ref="checkRebateComponent" />
              </div>

                <!-- 账单内容 -->
                <div v-show="activeTab === 'bill'" class="bill-content" style="display: none;">
                  <!-- 月度统计标题 -->
                  <div class="chart-header unified-header">
                    <div class="header-left">
                      <el-icon class="chart-icon"><TrendCharts /></el-icon>
                      <span class="chart-title"
                        >月度统计 (更新至: {{ currentDate }})</span
                      >
                    </div>
                    <div class="chart-actions">
                      <el-button
                        size="small"
                        :type="monthlyChartVisibility.charge ? 'primary' : ''"
                        @click="toggleMonthlyBar('charge')"
                      >
                        Charge
                      </el-button>
                      <el-button
                        size="small"
                        :type="monthlyChartVisibility.totalCost ? 'warning' : ''"
                        @click="toggleMonthlyBar('totalCost')"
                        class="orange-button"
                      >
                        实际花费
                      </el-button>
                    </div>
                  </div>

                  <!-- 图表区域 -->
                  <div class="chart-container unified-card">
                    <!-- ECharts图表 -->
                    <div
                      ref="chartContainer"
                      class="chart-view"
                      id="billChart"
                    ></div>

                    <!-- 底部说明文字 -->
                    <div class="chart-footer">
                      <div class="footer-text">
                        <span class="asterisk">*</span>点击柱状图查看月度账单详情
                      </div>
                    </div>
                  </div>

                  <!-- 统计卡片区域 -->
                  <div class="stats-section">
                    <div class="stats-row">
                      <!-- 当月费用占比分析 -->
                      <div class="stats-card">
                        <div class="card-header-small">
                          <div class="header-left">
                            <el-icon class="card-icon"><TrendCharts /></el-icon>
                            <span class="card-title">当月费用占比分析</span>
                            <span class="card-date">(更新至: {{ currentDate }})</span>
                          </div>
                        </div>
                        <div class="stats-cards">
                          <div
                            class="stat-item webdirect"
                            :class="{
                              active: selectedStatType === 'webdirect',
                            }"
                            @click="switchStatData('webdirect')"
                          >
                            <div class="stat-label">本月Charge:</div>
                            <div class="stat-value">
                              {{ currencySymbol }}{{ statsData.webdirect.webdirect }}
                            </div>
                          </div>
                          <div
                            class="stat-item package"
                            :class="{ active: selectedStatType === 'package' }"
                            @click="switchStatData('package')"
                          >
                            <div class="stat-label">本月实际花费:</div>
                            <div class="stat-value">
                              {{ currencySymbol }}{{ statsData.package.package }}
                            </div>
                          </div>
                        </div>
                        <div class="chart-container-small">
                          <div
                            ref="pieChart"
                            id="pieChart"
                            class="pie-chart"
                          ></div>
                        </div>
                      </div>

                      <!-- 年度统计图表 -->
                      <div class="yearly-chart">
                        <div class="card-header-small">
                          <el-icon class="card-icon"><DataAnalysis /></el-icon>
                          <span class="card-title">趋势统计图表</span>
                          <span class="card-date">(更新至: {{ currentDate }})</span>
                        </div>
                        <div class="yearly-stats">
                          <div class="stat-item-yearly webdirect-yearly" @click="toggleYearlyLine('charge')" :class="{ active: yearlyLineVisibility.charge }">
                            <div class="stat-label">全年Charge:</div>
                            <div class="stat-value">{{ currencySymbol }} {{ lineChartData ? lineChartData.chargeData.reduce((sum, val) => sum + val, 0).toFixed(2) : '0.00' }}</div>
                          </div>
                          <div class="stat-item-yearly package-yearly" @click="toggleYearlyLine('totalCost')" :class="{ active: yearlyLineVisibility.totalCost }">
                            <div class="stat-label">全年实际花费:</div>
                            <div class="stat-value">{{ currencySymbol }} {{ lineChartData ? lineChartData.totalCostData.reduce((sum, val) => sum + val, 0).toFixed(2) : '0.00' }}</div>
                          </div>
                        </div>
                        <div
                          ref="lineChart"
                          id="lineChart"
                          class="line-chart"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>



              <!-- Invoice监控内容 -->
              <div v-if="activeTab === 'settings'" class="settings-content">
                <InvoiceMonitor />
              </div>

              <!-- 整合表下载内容 -->
              <div v-show="activeTab === 'integration'" class="integration-content">
                  <!-- 搜索框区域 -->
                  <div class="integration-search-section">
                    <div class="search-grid">
                      <!-- Row 1 -->
                      <div class="search-item">
                        <label class="search-label">日期</label>
                        <el-date-picker
                          v-model="integrationForm.date"
                          type="month"
                          placeholder="选择月份"
                          format="YYYY-MM"
                          value-format="YYYY-MM"
                          class="search-input"
                        />
                      </div>
                      <div class="search-item">
                        <label class="search-label">销售</label>
                        <el-select
                          v-model="integrationForm.sales"
                          placeholder="请选择"
                          class="search-input"
                          clearable
                          filterable
                          :disabled="!isAdmin"
                        >
                          <el-option
                            v-for="item in filteredSalesList"
                            :key="item.id"
                            :label="item.name"
                            :value="item.name"
                          />
                        </el-select>
                      </div>
                      <div class="search-item">
                        <label class="search-label">伙伴</label>
                        <el-select
                          v-model="integrationForm.partner"
                          placeholder=""
                          class="search-input"
                          clearable
                          filterable
                        >
                          <el-option
                            v-for="item in filteredIntegrationAgentsList"
                            :key="item.recordId"
                            :label="`${item.company} (${item.agent_id})`"
                            :value="item.recordId"
                          />
                        </el-select>
                      </div>
                      <div class="search-item">
                        <label class="search-label">Payer</label>
                        <el-select
                          v-model="integrationForm.payer"
                          placeholder=""
                          class="search-input"
                          clearable
                          filterable
                        >
                          <el-option
                            v-for="item in filteredIntegrationPayersList"
                            :key="item.payer_id"
                            :label="item.name + '（' + item.payer_id + '）'"
                            :value="item.payer_id"
                          />
                        </el-select>
                      </div>
                      <!-- Row 2 -->
                      <div class="search-item">
                        <label class="search-label">地区</label>
                        <el-select
                          v-model="integrationForm.type"
                          placeholder="请选择地区"
                          class="search-input"
                        >
                          <el-option label="全部" value="all" />
                          <el-option label="global" value="global" />
                          <el-option label="cn" value="cn" />
                        </el-select>
                      </div>
                      <div class="search-item">
                        <label class="search-label">Link</label>
                        <el-select
                          v-model="integrationForm.link"
                          placeholder=""
                          class="search-input"
                          clearable
                          filterable
                          :filter-method="integrationLinkFilterMethod"
                          @focus="isIntegrationLinkFocused = true"
                          @blur="isIntegrationLinkFocused = false"
                        >
                          <el-option
                            v-for="item in displayedIntegrationLinksList"
                            :key="item.link_id"
                            :label="(item.customer_name || item.name || `客户${item.link_id}`) + '（linkID：' + item.link_id + '）'"
                            :value="item.link_id"
                          />
                        </el-select>
                      </div>
                      <div class="search-item">
                        <label class="search-label">对账单</label>
                        <el-select
                          v-model="integrationForm.detailBillType"
                          placeholder="请选择"
                          class="search-input"
                          clearable
                        >
                          <el-option label="伙伴对账单" value="agent" />
                          <el-option label="客户对账单" value="client" />
                        </el-select>
                      </div>
                      <div class="search-item">
                        <label class="search-label">账号类型</label>
                        <el-select
                          v-model="integrationForm.account_type"
                          placeholder="全部"
                          class="search-input"
                          clearable
                        >
                          <el-option label="全部" value="" />
                          <el-option label="Normal" value="normal" />
                          <el-option label="Billing Transfer" value="bt" />
                        </el-select>
                      </div>
                      <div class="search-actions">
                        <el-button type="primary" @click="searchIntegrationData" class="search-btn">
                          <el-icon><Search /></el-icon>
                          查询
                        </el-button>
                        <el-button @click="regenerateBills" class="regen-btn" :loading="regenerating">
                          <el-icon><RefreshRight /></el-icon>
                          {{ regenProgressText || '重新生成' }}
                        </el-button>
                      </div>
                    </div>
                  </div>

                  <!-- 表格区域 -->
                  <div class="integration-table-section">
                    <!-- 表格工具栏 -->
                    <div class="table-toolbar">
                      <div class="toolbar-left">
                        <span class="table-title">整合表核心数据</span>
                        <span class="table-count">共 {{ integrationTableData.length }} 条记录</span>
                      </div>
                      <div class="toolbar-right">
                        <el-button size="small" @click="showColumnSettings = true">
                          <el-icon><Setting /></el-icon>
                          自定义导出列
                        </el-button>
                        <span class="format-label">账单格式：</span>
                        <el-select 
                          v-model="billFormat" 
                          size="small" 
                          style="width: 100px;"
                          placeholder="格式"
                        >
                          <el-option label="PDF" value="pdf"></el-option>
                          <el-option label="Excel" value="excel"></el-option>
                        </el-select>
                        <el-button size="small" @click="exportTableData">
                          <el-icon><Download /></el-icon>
                          下载整合表
                        </el-button>
                        <el-button
                          v-if="hasPermission('download_bill')"
                          size="small"
                          @click="downloadBills"
                        >
                          <el-icon><Download /></el-icon>
                          下载账单
                        </el-button>
                        <el-button
                          v-if="hasPermission('download_bill')"
                          size="small"
                          @click="downloadReconciliation"
                        >
                          <el-icon><Download /></el-icon>
                          下载对账单
                        </el-button>
                        <el-button
                          v-if="hasPermission('download_bill')"
                          size="small"
                          @click="downloadInvoicePDF"
                        >
                          <el-icon><Download /></el-icon>
                          下载Invoice PDF
                        </el-button>
                        <el-button
                          size="small"
                          @click="downloadAll"
                        >
                          <el-icon><Download /></el-icon>
                          下载全部
                        </el-button>
                      </div>
                    </div>

                    <!-- 数据表格 -->
                    <div class="table-container">
                      <el-table
                        :data="integrationTableData"
                        style="width: 100%;"
                        stripe
                        max-height="500"
                        v-loading="integrationLoading"
                      >
                        <el-table-column
                          v-for="column in tableColumns"
                          :key="column.prop"
                          :prop="column.prop"
                          :label="column.label"
                          show-overflow-tooltip
                        >
                          <template #default="scope">
                            <span>{{ formatColumnValue(scope.row, column) }}</span>
                          </template>
                        </el-table-column>
                        </el-table>
                    </div>
                  </div>

                  <!-- 列设置弹窗 -->
                  <el-dialog
                    v-model="showColumnSettings"
                    title="自定义导出列设置"
                    width="600px"
                    :before-close="handleColumnSettingsClose"
                  >
                    <div class="column-settings">
                      <div class="settings-header">
                        <span>选择要导出的列（拖拽可调整顺序）</span>
                        <div class="settings-actions">
                          <el-button size="small" @click="selectAllColumns">全选</el-button>
                          <el-button size="small" @click="clearAllColumns">清空</el-button>
                          <el-button size="small" @click="resetDefaultColumns">恢复默认</el-button>
                        </div>
                      </div>
                      <div class="column-list">
                        <draggable
                          v-model="columnSettings"
                          item-key="prop"
                          @end="onColumnDragEnd"
                        >
                          <template #item="{ element }">
                            <div class="column-item">
                              <el-checkbox
                                v-model="element.visible"
                                :label="element.label"
                              >
                                {{ element.label }}
                              </el-checkbox>
                              <el-icon class="drag-handle"><Rank /></el-icon>
                            </div>
                          </template>
                        </draggable>
                      </div>
                      <div style="margin-top: 20px; text-align: right;">
                        <el-button @click="handleColumnSettingsClose">取消</el-button>
                        <el-button type="primary" @click="applyColumnSettings">应用</el-button>
                      </div>
                    </div>
                  </el-dialog>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Invoice上传对话框 -->
    <el-dialog
      v-model="invoiceUploadDialogVisible"
      title="上传Invoice文件"
      width="600px"
    >
      <!-- 月份选择 -->
      <div style="margin-bottom: 15px;">
        <el-form :inline="true">
          <el-form-item label="账单月份:">
            <el-date-picker
              v-model="invoiceMonth"
              type="month"
              placeholder="选择月份"
              format="YYYY-MM"
              value-format="YYYYMM"
              :clearable="false"
              style="width: 200px;"
            />
          </el-form-item>
        </el-form>
      </div>
      <div 
        class="invoice-upload-area"
        @drop.prevent="handleFolderDrop"
        @dragover.prevent
        @dragenter.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        :class="{ 'is-dragging': isDragging }"
      >
        <div class="custom-upload-box" @click="triggerFileSelect">
          <el-icon class="el-icon--upload"><upload-filled /></el-icon>
          <div class="el-upload__text">将PDF文件或文件夹拖到此处，或<em>点击选择文件</em></div>
          <div class="el-upload__tip">PDF文件可批量拖拽，文件夹只能单个拖拽上传（最多20个文件）</div>
        </div>
        <input 
          ref="fileInput" 
          type="file" 
          multiple 
          accept=".pdf"
          style="display: none" 
          @change="handleFileSelect"
        />
      </div>
      <div v-if="invoiceFileList.length > 0" class="file-list">
        <div class="file-count">已选择 {{ invoiceFileList.length }} 个文件</div>
        <div v-for="(file, index) in invoiceFileList" :key="index" class="file-item">
          <span class="file-path" :title="file.fullPath">{{ file.fullPath || file.name }}</span>
          <el-icon class="remove-icon" @click="removeFile(index)"><Close /></el-icon>
        </div>
      </div>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="invoiceUploadDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitInvoiceUpload" :loading="invoiceUploading">
            开始上传并识别
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>



<script>
import {getPayerList,getAccountList,listPolicyAws,attachAwsPolicyWithLink,detachAwsPolicyWithLink,listPolicy,addPolicy,removePolicy,
        getInviteStatus,inviteAccount,inviteAccountLocal,inviteList,deleteInvite,agreeInvite,denyInvite,removeAccountFromOrg,closeAccountFromOrg,
        getAgentList,getSalesList,createOU,deleteOU,listPolicyForTarget,listRoots,listOU,listChildren,getLinkerList,getBillList,getProductAggregation,
        downloadBillByUrl,downloadBillDetail,sendZhangdan} from '@/api/api'
import { useBillDataStore } from '@/store/billData'
import * as echarts from "echarts";
import { ElMessage } from "element-plus";
import {
  Document,
  FolderOpened,
  TrendCharts,
  Setting,
  QuestionFilled,
  DataAnalysis,
  Location,
  Download,
  Search,
  UploadFilled,
  Upload,
  Grid,
  CircleCheck,
  Rank,
  InfoFilled,
  Close,
  RefreshRight
} from "@element-plus/icons-vue";
import draggable from 'vuedraggable';
import { hasPermission } from '@/utils/permission';
import CheckRebate from '@/views/checkRebate/checkRebate.vue';
import CurBillManage from './CurBillManage.vue';
import InvoiceMonitor from './InvoiceMonitor.vue';

/*
 * 账单管理页面优化版本
 *
 * 主要优化点：
 * 1. 树节点点击时的请求优化：
 *    - agent节点：只传agent_id，后端聚合所有子节点数据
 *    - payer节点：传agent_id+payer_id，后端聚合该payer下所有link数据
 *    - link节点：传完整参数，查询单个link数据
 *
 * 2. 减少网络请求：
 *    - 原方案：一个payer下有N个link时，需要发送N个请求
 *    - 优化方案：只发送1个聚合请求，由后端处理数据聚合
 *
 * 3. 参数传递简化：
 *    - 参考billManage_back.vue的实现
 *    - 移除不必要的参数过滤逻辑
 */

export default {
  name: "BillManage",
  components: {
    Document,
    FolderOpened,
    TrendCharts,
    Setting,
    QuestionFilled,
    DataAnalysis,
    Location,
    Download,
    Search,
    UploadFilled,
    Rank,
    InfoFilled,
    Close,
    RefreshRight,
    draggable,
    CheckRebate,
    CurBillManage,
    InvoiceMonitor
  },
  data() {
    return {
      // 搜索表单数据
      formInline: {
        billType: 1,      // 账单类型，默认为总代应付
        sales: '',        // 选择销售
        selected_agent_record_id: '', // 选择伙伴，现在存储recordId
        payer_id: '',     // 选择payer
        link_id: '',      // 选择link
        page: 1,
        limit: 20
      },

      // 下拉选项数据
      billTypeList: [
        {name:'总代应付',value:1},
        {name:'伙伴应付',value:2},
        {name:'客户应付',value:3},
      ],
      salesList: [],      // 销售列表
      agentsList: [],     // 伙伴列表 (原始数据)
      payersList: [],     // Payer列表
      linksList: [],      // Link列表
      displayedLinksList: [], // 显示的Link列表
      linkSearchQuery: '', // Link搜索查询
      linkFilterTimer: null, // 防抖定时器
      isLinkFocused: false, // 是否聚焦状态
      displayedBatchLinksList: [], // 批量下载显示的Link列表
      batchLinkSearchQuery: '', // 批量下载Link搜索查询
      batchLinkFilterTimer: null, // 批量下载防抖定时器
      isBatchLinkFocused: false, // 批量下载是否聚焦状态

      // 原始数据备份
      originalEntityTreeData: [],

      // 默认展开的节点
      defaultExpandedKeys: [],

      // 用户角色（响应式，避免 computed 无法追踪 localStorage 变化）
      curRole: (() => {
        let role = localStorage.getItem('curRole') || '';
        if (!role) {
          try {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            const typeMap = { 1: 'admin', 2: 'agent', 3: 'sales', 4: 'customer' };
            role = typeMap[userInfo.type] || '';
          } catch(e) {}
        }
        return role;
      })(),

      // Tab相关
      activeTab: "cur",
      
      // 账单分析视图链接
      billAnalysisViewUrl: '/billAnalysisView',

      // 图表类型
      chartType: "day",

      // 统计卡片相关数据
      selectedStatType: "webdirect",
      statsData: {
        webdirect: {
          webdirect: "0.00", // Charge值
          package: "0.00",   // 实际花费值
          pieData: [],
        },
        package: {
          webdirect: "0.00", // Charge值
          package: "0.00",   // 实际花费值
          pieData: [],
        },
      },

      // 树形控件配置
      treeProps: {
        label: "label",
        children: "children",
        isLeaf: "isLeaf"
      },

      // 实体树数据
      entityTreeData: [],

      // 图表实例
      chartInstance: null,
      pieChartInstance: null,
      lineChartInstance: null,

      // 账单设置
      billSettings: {
        sales: '',
        timePeriod: "lastMonth",
        partnerGranularity: "all",
        selectedPartner: '',
        selectedPayer: '',
        selectedLink: '',
        reportFormat: "pdf",
      },

      // Invoice校验设置
      invoiceSettings: {
        enabled: true,
        uploadedFiles: []
      },

      // 自定义账单设置
      logoSettings: {
        selectedPartner: '',
        billAddress: '',
        uploadedLogos: []
      },

      // 上传相关
      uploadAction: '/api/bill/invoice/upload/',
      logoUploadAction: '/api/upload/logo',
      invoiceUploadDialogVisible: false,
      invoiceMonth: '',  // 将在created中初始化为上个月
      invoiceFileList: [],
      invoiceUploading: false,
      isDragging: false,

      // 图表数据
      chartData: {
        months: [
          "2025-01",
          "2025-02",
          "2025-03",
          "2025-04",
          "2025-05",
          "2025-06",
          "2025-07",
          "2025-08",
          "2025-09",
          "2025-10",
        ],
        webDirectData: [
          256.55, 235.25, 255.41, 2449.65, 255.37, 248.59, 255.41, 255.41,
          74.36, 218.9,
        ],
        supportData: [
          256.55, 235.25, 255.41, 2449.65, 255.37, 248.59, 255.41, 255.41,
          74.36, 218.9,
        ], // 套餐计算数据
      },

      // 折线图数据
      lineChartData: null,

      // 系列可见性跟踪
      seriesVisibility: {
        webdirect: false,
        support: true,
      },

      // 选中的树节点信息
      selectedTreeNode: null,

      // 年度统计图表折线可见性
      yearlyLineVisibility: {
        charge: true,
        totalCost: true
      },

      // 当前日期
      currentDate: '',

      // 月度统计柱状图可见性
      monthlyChartVisibility: {
        charge: true,
        totalCost: true
      },

      // 加载状态
      loading: true,

      // 搜索防抖定时器
      searchTimer: null,

      // 数据索引
      payersMap: new Map(),
      linksByAgentMap: new Map(),
      linksByPayerMap: new Map(),
      // 新增：按agent表记录索引
      agentRecordsMap: new Map(), // 存储所有agent记录，key为recordId

      // 汇率配置
      exchangeRate: 7.0, // 人民币对美元汇率，启动时从后端获取

      // 当前货币信息（方案二：动态货币符号）
      currentCurrency: {
        symbol: '¥',        // 当前货币符号
        code: 'CNY',        // 货币代码
        region: null,       // 当前region
        isMixed: false      // 是否混合货币（agent节点包含多个region）
      },

      // 是否使用懒加载模式
      isLazyMode: true,

      // 整合表下载相关数据
      integrationForm: {
        date: '',  // 将在created中初始化为上个月
        sales: '',
        partner: '',
        payer: '',
        type: 'global',
        link: '',  // 新增：Link筛选
        detailBillType: 'client',  // 对账单版本筛选，默认为客户对账单
        account_type: ''  // 账号类型筛选：''=全部, 'bt'=BT, 'normal'=非BT
      },
      billFormat: 'pdf',  // 新增：账单格式，默认PDF
      displayedIntegrationLinksList: [],  // 新增：显示的Link列表
      integrationLinkSearchQuery: '',     // 新增：Link搜索查询
      integrationLinkFilterTimer: null,   // 新增：防抖定时器
      isIntegrationLinkFocused: false,    // 新增：是否聚焦

      // 整合表数据
      integrationTableData: [],
      integrationLoading: false,
      regenerating: false,
      regenProgressText: '',
      integrationPagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      },

      // 表格显示列（固定，不受自定义列影响）
      tableColumns: [
        { prop: 'payer_id', label: 'Payerid' },
        { prop: 'link_id', label: 'Linkid' },
        { prop: 'charges', label: 'Charge' },
        { prop: 'credits', label: 'Credits' },
        { prop: 'savings_plan', label: 'Savings Plan', formatter: 'removeBrackets' },
        { prop: 'bundle', label: 'Bundle', formatter: 'removeBrackets' },
        { prop: 'tax', label: 'Tax' },
        { prop: 'vat', label: 'Discounts%', formatter: 'calculateDiscountPercent' },
        { prop: 'distribution', label: 'Discount', formatter: 'removeBrackets' },
        { prop: 'total', label: 'TotalCost' }
      ],
      
      // 列设置（仅用于导出Excel）
      showColumnSettings: false,
      columnSettings: [
        { prop: 'po', label: 'PO', visible: true },
        { prop: 'payerId', label: 'PayerAccountid', visible: true },
        { prop: 'linkedAccountId', label: 'LinkedAccountid', visible: true },
        { prop: 'linkedName', label: 'CustomerName', visible: true },
        { prop: 'billingPeriodStartDate', label: 'BillingPeriodStartDate', visible: true },
        { prop: 'billingPeriodEndDate', label: 'BillingPeriodEndDate', visible: true },
        { prop: 'actualUsage', label: 'Charge(USD)', visible: true },
        { prop: 'creditsDeduction', label: 'Credits(USD)', visible: true },
        { prop: 'savingsPlanDiscount', label: 'Savings Plan', visible: true },
        { prop: 'rebateRatio', label: 'Bundled Discount', visible: true },
        { prop: 'rebateAmount', label: 'TaxAmount(USD)', visible: true },
        { prop: 'vat', label: 'Discounts%', visible: true },
        { prop: 'payableAmount', label: 'Distributor Discounts(USD)', visible: true },
        { prop: 'totalCost', label: 'TotalCost(USD)', visible: true }
      ]
    };
  },

  created() {
    console.log('=== billManage created ===');
    console.log('当前用户角色:', localStorage.getItem('curRole'));
    console.log('当前用户账号:', localStorage.getItem('account'));
    
    // 刷新响应式 curRole，兜底从 userInfo.type 推断
    let curRole = localStorage.getItem('curRole') || '';
    if (!curRole) {
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const typeMap = { 1: 'admin', 2: 'agent', 3: 'sales', 4: 'customer' };
        curRole = typeMap[userInfo.type] || '';
        if (curRole) {
          localStorage.setItem('curRole', curRole);
          console.log('从 userInfo.type 推断角色:', curRole);
        }
      } catch(e) {}
    }
    this.curRole = curRole;
    
    // 初始化默认日期为上个月
    const lastMonth = this.getLastMonth();
    this.integrationForm.date = lastMonth;
    this.invoiceMonth = lastMonth.replace('-', '');
    
    this.currentDate = this.getCurrentDate();
    this.loadExchangeRate();
    this.loadBaseDataFromStore();
    this.initUserPermissions();
    
    // 拦截 ResizeObserver 错误 - 在 created 阶段就设置
    const originalError = console.error;
    console.error = function(...args) {
      if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
        return;
      }
      originalError.apply(console, args);
    };
    
    // 拦截 handleError 函数
    window.addEventListener('unhandledrejection', event => {
      if (event.reason && event.reason.message && event.reason.message.includes('ResizeObserver')) {
        event.preventDefault();
      }
    });
  },

  mounted() {
    this.initChart();
    this.initPieChart();
    this.initLineChart();

      // 默认选中第一个统计类型
    this.selectedStatType = "webdirect"; // 默认显示charge

    // 初始化默认数据显示
    this.initDefaultChartData();
  },

  watch: {
    'integrationForm.sales'() {
      // 销售变化时清空下级筛选
      this.integrationForm.partner = '';
      this.integrationForm.payer = '';
      this.integrationForm.link = '';
    },
    'integrationForm.partner'() {
      // 伙伴变化时清空下级筛选
      this.integrationForm.payer = '';
      this.integrationForm.link = '';
    },
    'integrationForm.payer'() {
      // Payer变化时清空Link筛选
      this.integrationForm.link = '';
    }
  },

  beforeUnmount() {
    // 移除事件监听
    window.removeEventListener("resize", this.handleResize);

    // 清理搜索定时器
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }

    // 清理Link过滤定时器
    if (this.linkFilterTimer) {
      clearTimeout(this.linkFilterTimer);
      this.linkFilterTimer = null;
    }

    // 清理批量下载Link过滤定时器
    if (this.batchLinkFilterTimer) {
      clearTimeout(this.batchLinkFilterTimer);
      this.batchLinkFilterTimer = null;
    }

    // 清理数据索引
    this.payersMap.clear();
    this.linksByAgentMap.clear();
    this.linksByPayerMap.clear();
    this.agentRecordsMap.clear();

    // 销毁图表实例
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
    if (this.pieChartInstance) {
      this.pieChartInstance.dispose();
      this.pieChartInstance = null;
    }
    if (this.lineChartInstance) {
      this.lineChartInstance.dispose();
      this.lineChartInstance = null;
    }
  },

  computed: {
    // 是否为管理员
    isAdmin() {
      return this.curRole === 'admin'
    },
    // 是否显示所有Tab (admin和sales)
    showAllTabs() {
      return this.curRole === 'admin' || this.curRole === 'sales'
    },
    // 是否显示"返点核对"Tab：管理员/销售始终显示；其他角色（伙伴/客户等）
    // 若在权限管理里被授予了 checkRebate 菜单则显示。检查依据：登录时缓存到
    // localStorage 的 menuList（apiSystemWebRouter 结果，仅包含当前角色被绑的菜单）
    showRebateTab() {
      if (this.showAllTabs) return true
      try {
        const menuList = JSON.parse(localStorage.getItem('menuList') || '[]')
        return menuList.some(m => m && (m.url === 'checkRebate' || m.moduleName === '返点核对'))
      } catch (e) {
        return false
      }
    },
    // 当前选中的统计数据
    currentStats() {
      return this.statsData[this.selectedStatType];
    },

    // 当前饼图数据
    currentPieData() {
      return this.statsData[this.selectedStatType].pieData;
    },

    // 动态计算柱子宽度
    dynamicBarWidth() {
      // 基于系列可见性计算活跃系列数量
      const visibleSeriesCount =
        (this.seriesVisibility.webdirect ? 1 : 0) +
        (this.seriesVisibility.support ? 1 : 0);

      // 如果只有一个系列可见，宽度为80；如果两个系列都可见，每个宽度为40
      return visibleSeriesCount === 1 ? 80 : 40;
    },

    // 过滤后的销售列表（排除test）
    filteredSalesList() {
      return this.salesList.filter(item => item.name !== 'test');
    },

    // 为el-select准备的agentsList，包含recordId
    agentsListWithRecordId() {
      return this.agentsList.map((agent, index) => ({
        ...agent,
        recordId: `${agent.agent_id}_${agent.company}_${agent.id || ''}_${index}`, // 确保recordId的唯一性
      }));
    },

    // 过滤后的伙伴列表(用于批量下载)
    filteredPartnersList() {
      if (!this.billSettings.sales) {
        return this.agentsListWithRecordId;
      }
      return this.agentsListWithRecordId.filter(agent => agent.sales === this.billSettings.sales);
    },

    // 可见的列（用于导出Excel）
    visibleColumns() {
      return this.columnSettings.filter(col => col.visible);
    },

    // 获取当前货币符号
    currencySymbol() {
      return this.currentCurrency.symbol;
    },

    // 获取当前货币代码
    currencyCode() {
      return this.currentCurrency.code;
    },

    // 是否为混合货币
    isMixedCurrency() {
      return this.currentCurrency.isMixed;
    },

    // 根据销售过滤伙伴列表（整合表）
    filteredIntegrationAgentsList() {
      if (!this.integrationForm.sales) {
        return this.agentsListWithRecordId;
      }
      return this.agentsListWithRecordId.filter(agent => agent.sales === this.integrationForm.sales);
    },

    // 根据伙伴过滤Payer列表（整合表）
    filteredIntegrationPayersList() {
      if (!this.integrationForm.partner) {
        return this.payersList;
      }
      const agent = this.agentRecordsMap.get(this.integrationForm.partner);
      if (!agent || !agent.payers) return [];
      
      const payerIds = agent.payers.split(',').map(id => id.trim()).filter(id => id);
      return this.payersList.filter(payer => payerIds.includes(payer.payer_id));
    },

    // 根据销售、伙伴、Payer过滤Link列表（整合表）
    filteredIntegrationLinksList() {
      let filtered = this.linksList;
      
      if (this.integrationForm.sales) {
        filtered = filtered.filter(link => link.sales === this.integrationForm.sales);
      }
      
      if (this.integrationForm.partner) {
        const agent = this.agentRecordsMap.get(this.integrationForm.partner);
        if (agent) {
          filtered = filtered.filter(link => link.agent_id === agent.agent_id);
        }
      }
      
      if (this.integrationForm.payer) {
        filtered = filtered.filter(link => link.payer_id === this.integrationForm.payer);
      }
      
      return filtered;
    }
  },

  methods: {
    formatMonth(month) {
      if (!month || month.length !== 6) return month;
      return month.substring(0, 4) + '年' + month.substring(4, 6) + '月';
    },
    hasPermission,
    // 初始化用户权限
    initUserPermissions() {
      const curRole = this.curRole
      
      // 如果是销售，自动设置为当前用户的销售名称
      if (curRole === 'sales') {
        const account = localStorage.getItem('account')
        if (account) {
          this.integrationForm.sales = account
          console.log('销售登录，自动设置销售为:', account)
        }
      }
    },
    // 获取上个月（YYYY-MM格式）
    getLastMonth() {
      const now = new Date()
      const currentYear = now.getFullYear()
      const currentMonth = now.getMonth() + 1 // 1-12
      
      let lastYear = currentYear
      let lastMonth = currentMonth - 1
      
      if (lastMonth === 0) {
        lastYear = currentYear - 1
        lastMonth = 12
      }
      
      return `${lastYear}-${String(lastMonth).padStart(2, '0')}`
    },
    // 加载汇率配置
    async loadExchangeRate() {
      try {
        const res = await fetch('/api/bill/config/exchange-rate/');
        const data = await res.json();
        if (data.code === 2000 && data.data) {
          this.exchangeRate = data.data.cny_to_usd || 7.0;
          console.log('汇率加载成功:', this.exchangeRate);
        }
      } catch (error) {
        console.error('加载汇率失败，使用默认值7.0:', error);
        this.exchangeRate = 7.0;
      }
    },

    // 从store加载基础数据
    async loadBaseDataFromStore() {
      const billDataStore = useBillDataStore();
      
      if (billDataStore.loaded) {
        // 直接使用store中的数据
        this.agentsList = billDataStore.agents;
        this.payersList = billDataStore.payers;
        this.linksList = billDataStore.links;
        this.salesList = billDataStore.sales;
        
        console.log('billManage: 伙伴数据加载完成', this.agentsList.length, '条记录');
        console.log('billManage: AWG CLOUD搜索结果', this.agentsList.filter(a => a.company && a.company.includes('AWG')));
        
        this.buildDataMaps();
        this.displayedLinksList = [];
        this.loading = false;
        this.filterEntityTree();
      } else {
        // 等待store加载完成
        this.loading = true;
        await billDataStore.loadData();
        this.loadBaseDataFromStore();
      }
    },

    // 初始化默认图表数据
    initDefaultChartData() {
      // 设置默认的月度数据（当前年份的12个月）
      const currentYear = new Date().getFullYear();
      const defaultMonths = [];
      const currentMonth = new Date().getMonth() + 1; // 获取当前月份 (1-12)
      for (let month = 1; month <= currentMonth; month++) {
        defaultMonths.push(`${currentYear}-${month.toString().padStart(2, '0')}`);
      }

      // 初始化默认图表数据
      this.chartData = {
        months: defaultMonths,
        webDirectData: new Array(currentMonth).fill(0), // 当前月份的默认值为0
        supportData: new Array(currentMonth).fill(0) // 当前月份的默认值为0
      };

      // 初始化默认折线图数据
      this.lineChartData = {
        months: [],
        chargeData: [],
        totalCostData: [],
        growthRates: []
      };

      // 更新图表显示
      this.$nextTick(() => {
        this.updateChart();
        this.updatePieChart();
        this.updateLineChart();
      });
    },

    // 处理Link选择框聚焦
    handleLinkFocus() {
      this.isLinkFocused = true;
      // 聚焦时如果没有搜索内容，不显示任何选项
      if (!this.linkSearchQuery || this.linkSearchQuery.trim() === '') {
        this.displayedLinksList = [];
      }
    },

    // 处理Link选择框失焦
    handleLinkBlur() {
      this.isLinkFocused = false;
      // 延迟清空，避免选择时立即清空
      setTimeout(() => {
        if (!this.isLinkFocused) {
          this.displayedLinksList = [];
        }
      }, 200);
    },

    // 自定义Link过滤方法 - 优化版本
    linkFilterMethod(query) {
      // 清除之前的定时器
      if (this.linkFilterTimer) {
        clearTimeout(this.linkFilterTimer);
      }

      // 使用防抖处理，减少卡顿
      this.linkFilterTimer = setTimeout(() => {
        this.performLinkFilter(query);
      }, 150); // 150ms防抖延迟
    },

    // 执行Link过滤
    performLinkFilter(query) {
      this.linkSearchQuery = query;

      if (!query || query.trim() === '') {
        this.displayedLinksList = [];
        return;
      }

      const searchQuery = String(query).trim();
      this.displayedLinksList = this.linksList.filter(item => {
        const linkIdStr = String(item.link_id);
        const linkIdMatch = linkIdStr.includes(searchQuery);
        const customerName = item.customer_name || item.name || `客户${item.link_id}`;
        const customerNameMatch = customerName.toLowerCase().includes(searchQuery.toLowerCase());
        return linkIdMatch || customerNameMatch;
      });

      if (this.displayedLinksList.length > 50) {
        this.displayedLinksList = this.displayedLinksList.slice(0, 50);
      }
    },

    // 批量下载Link选择框聚焦
    handleBatchLinkFocus() {
      this.isBatchLinkFocused = true;
      if (!this.batchLinkSearchQuery || this.batchLinkSearchQuery.trim() === '') {
        this.displayedBatchLinksList = [];
      }
    },

    // 批量下载Link选择框失焦
    handleBatchLinkBlur() {
      this.isBatchLinkFocused = false;
      setTimeout(() => {
        if (!this.isBatchLinkFocused) {
          this.displayedBatchLinksList = [];
        }
      }, 200);
    },

    // 批量下载自定义Link过滤方法
    batchLinkFilterMethod(query) {
      if (this.batchLinkFilterTimer) {
        clearTimeout(this.batchLinkFilterTimer);
      }
      this.batchLinkFilterTimer = setTimeout(() => {
        this.performBatchLinkFilter(query);
      }, 150);
    },

    // 执行批量下载Link过滤
    performBatchLinkFilter(query) {
      this.batchLinkSearchQuery = query;

      if (!query || query.trim() === '') {
        this.displayedBatchLinksList = [];
        return;
      }

      const searchQuery = String(query).trim();
      this.displayedBatchLinksList = this.linksList.filter(item => {
        const linkIdStr = String(item.link_id);
        const linkIdMatch = linkIdStr.includes(searchQuery);
        const customerName = item.customer_name || item.name || `客户${item.link_id}`;
        const customerNameMatch = customerName.toLowerCase().includes(searchQuery.toLowerCase());
        return linkIdMatch || customerNameMatch;
      });

      if (this.displayedBatchLinksList.length > 50) {
        this.displayedBatchLinksList = this.displayedBatchLinksList.slice(0, 50);
      }
    },

    // 计算预计的树节点总数
    calculateTotalNodes() {
      const agentCount = this.agentsList?.length || 0;
      const payerCount = this.payersList?.length || 0;
      const linkCount = this.linksList?.length || 0;

      // 预估总节点数：agent + payer + link
      return agentCount + payerCount + linkCount;
    },

    // 获取销售列表
    async getSalesList() {
      try {
        const res = await getSalesList({name: '', page: 1, limit: 999999});
        if (res.code === 2000) {
          this.salesList = res.data.data || res.data || [];
          console.log("getSalesList: 销售列表获取成功", this.salesList);
        } else {
          console.error("getSalesList: 获取销售列表失败", res);
          this.salesList = [];
        }
      } catch (error) {
        console.error("getSalesList: 获取销售列表异常:", error);
        this.salesList = [];
      }
    },

    // 获取伙伴列表
    async getAgentsList() {
      let res = null; // 声明res并初始化为null
      try {
        res = await getAgentList({page: 1, limit: 999999});
        if (res.code === 2000) {
          this.agentsList = res.data.data || res.data;
          console.log("getAgentsList: 伙伴列表获取成功", this.agentsList);
        } else {
          console.error("getAgentsList: 获取伙伴列表失败", res);
          this.agentsList = [];
        }
      } catch (error) {
        console.error("getAgentsList: 获取伙伴列表异常:", error);
        this.agentsList = [];
      }
      return res;
    },

    // 获取Payer列表
    async getPayersList() {
      let res = null; // 声明res并初始化为null
      try {
        res = await getPayerList({page: 1, limit: 999999});
        if (res.code === 2000) {
          this.payersList = res.data.data || res.data;
          console.log("getPayersList: Payer列表获取成功", this.payersList);
        } else {
          console.error("getPayersList: 获取Payer列表失败", res);
          this.payersList = [];
        }
      } catch (error) {
        console.error("getPayersList: 获取Payer列表异常:", error);
        this.payersList = [];
      }
      return res;
    },

    // 获取Link列表
    async getLinksList() {
      let res = null; // 声明res并初始化为null
      try {
        res = await getLinkerList({page: 1, limit: 999999});
        if (res.code === 2000) {
          this.linksList = res.data.data || res.data;
          console.log("getLinksList: Link列表获取成功", this.linksList);
        } else {
          console.error("getLinksList: 获取Link列表失败", res);
          this.linksList = [];
        }
      } catch (error) {
        console.error("getLinksList: 获取Link列表异常:", error);
        this.linksList = [];
      }
      return res;
    },

    // 构建数据索引 - 修改为支持重复agent_id
    buildDataMaps() {
      if (this.agentRecordsMap.size > 0) return; // 避免重复构建

      // 为每个agent记录创建唯一标识并存储
      this.agentsList.forEach((agent, index) => {
        const recordId = `${agent.agent_id}_${agent.company}_${agent.id || ''}_${index}`; // 确保recordId的唯一性
        this.agentRecordsMap.set(recordId, {...agent, recordId}); // 存储带recordId的完整agent对象
      });

      this.payersList.forEach(payer => {
        this.payersMap.set(payer.payer_id, payer);
      });

      this.linksList.forEach(link => {
        if (!this.linksByAgentMap.has(link.agent_id)) {
          this.linksByAgentMap.set(link.agent_id, []);
        }
        this.linksByAgentMap.get(link.agent_id).push(link);

        const payerKey = `${link.agent_id}_${link.payer_id}`;
        if (!this.linksByPayerMap.has(payerKey)) {
          this.linksByPayerMap.set(payerKey, []);
        }
        this.linksByPayerMap.get(payerKey).push(link);
      });
    },

    // 懒加载节点
    loadNode(node, resolve) {
      // 确保索引已构建
      this.buildDataMaps();

      if (node.level === 0) {
        resolve(this.getFilteredAgents());
        return;
      }

      if (node.data.type === 'agent') {
        resolve(this.getPayerNodes(node.data.recordId)); // 传递recordId
        return;
      }

      if (node.data.type === 'payer') {
        resolve(this.getLinkNodes(node.data.agent_id, node.data.payer_id));
        return;
      }

      resolve([]);
    },

    // 获取过滤后的Agent节点 - 修改为每个记录独立节点
    getFilteredAgents() {
      let agentsToDisplay = Array.from(this.agentRecordsMap.values()); // 获取所有带recordId的agent记录

      // 根据销售筛选
      if (this.formInline.sales) {
        agentsToDisplay = agentsToDisplay.filter(agent => agent.sales === this.formInline.sales);
      }

      // 根据选中的特定agent记录进行筛选
      if (this.formInline.selected_agent_record_id) {
        agentsToDisplay = agentsToDisplay.filter(agent => agent.recordId === this.formInline.selected_agent_record_id);
      }

      const treeData = [];
      const expandedKeys = [];
      const uniquePayersInTree = new Set(); // 用于跟踪已添加到树中的payer

      return agentsToDisplay.map(agent => ({
        id: `agent_${agent.recordId}_${agent.agent_id}`,
        label: `${agent.company} (${agent.agent_id})`,
        type: 'agent',
        agent_id: agent.agent_id,
        recordId: agent.recordId, // 传递recordId
        payers: agent.payers, // 使用该记录的payers字段
        isLeaf: false
      }));
    },

    // 获取Payer节点 - 修改为使用特定agent记录的payers，并添加去重逻辑
    getPayerNodes(recordId) {
      const agentRecord = this.agentRecordsMap.get(recordId);
      if (!agentRecord || !agentRecord.payers) {
        return [];
      }

      // 使用该agent记录的payers字段
      const payerIds = agentRecord.payers.split(',').map(id => id.trim()).filter(id => id);

      let filteredPayerIds = payerIds;
      if (this.formInline.payer_id) {
        filteredPayerIds = payerIds.filter(id => id === this.formInline.payer_id);
      }

      // 去重：使用Set确保每个payer_id只出现一次
      const uniquePayerIds = [...new Set(filteredPayerIds)];

      const payerNodes = [];
      const seenPayerIds = new Set();

      uniquePayerIds.forEach(payerId => {
        if (seenPayerIds.has(payerId)) return; // 避免重复添加
        seenPayerIds.add(payerId);

        const payer = this.payersMap.get(payerId);
        if (!payer) return;

        payerNodes.push({
          id: `payer_${recordId}_${payerId}`,
          label: `${payer.name} (${payer.payer_id})`,
          type: 'payer',
          payer_id: payerId,
          agent_id: agentRecord.agent_id,
          recordId: recordId,
          isLeaf: false
        });
      });
      return payerNodes;
    },

    // 获取Link节点
    getLinkNodes(agentId, payerId) {
      const payerKey = `${agentId}_${payerId}`;
      let links = this.linksByPayerMap.get(payerKey) || [];

      if (this.formInline.link_id) {
        links = links.filter(link => link.link_id === this.formInline.link_id);
      }

      return links.map(link => {
        const customerName = link.customer_name || link.name || `客户${link.link_id}`;
        return {
          id: `link_${agentId}_${payerId}_${link.link_id}`,
          label: `${customerName} (LinkID: ${link.link_id})`,
          type: 'link',
          link_id: link.link_id,
          payer_id: payerId,
          agent_id: agentId,
          customer_name: customerName,
          isLeaf: true
        };
      });
    },

    // 筛选EA树 - 重新构建根节点
    filterEntityTree() {
      console.log("filterEntityTree: 开始筛选实体树...");
      const hasFilters = this.formInline.sales || this.formInline.selected_agent_record_id || this.formInline.payer_id || this.formInline.link_id;
      console.log("filterEntityTree: hasFilters = ", hasFilters);

      if (hasFilters && (this.formInline.payer_id || this.formInline.link_id)) {
        console.log("filterEntityTree: 存在payer或link筛选条件，构建完整三级结构。");
        this.buildFilteredFullTree();
        this.defaultExpandedKeys = [];
      } else {
        console.log("filterEntityTree: 使用懒加载模式。");
        this.isLazyMode = true;
        this.entityTreeData = this.getFilteredAgents();
        this.defaultExpandedKeys = [];
      }
      console.log("filterEntityTree: 实体树筛选完成，entityTreeData = ", this.entityTreeData);
    },

    // 构建筛选后的完整三级树 - 彻底修复payer重复问题
    buildFilteredFullTree() {
      this.buildDataMaps();
      this.isLazyMode = false;

      let agentsToProcess = Array.from(this.agentRecordsMap.values());

      // 根据销售筛选
      if (this.formInline.sales) {
        agentsToProcess = agentsToProcess.filter(agent => agent.sales === this.formInline.sales);
      }

      // 根据选中的特定agent记录进行筛选
      if (this.formInline.selected_agent_record_id) {
        agentsToProcess = agentsToProcess.filter(agent => agent.recordId === this.formInline.selected_agent_record_id);

        // 如果筛选后没有agent，直接返回空树
        if (agentsToProcess.length === 0) {
          this.entityTreeData = [];
          this.defaultExpandedKeys = [];
          return;
        }
      }

      const treeData = [];
      const expandedKeys = [];

      agentsToProcess.forEach(agentRecord => {
        // 使用该agent记录的payers字段
        if (!agentRecord.payers) return;

        const payerIds = agentRecord.payers.split(',').map(id => id.trim()).filter(id => id);
        let filteredPayerIds = payerIds;

        if (this.formInline.payer_id) {
          filteredPayerIds = payerIds.filter(id => id === this.formInline.payer_id);
        }

        // 去重：使用Set确保每个payer_id只出现一次
        const uniquePayerIds = [...new Set(filteredPayerIds)];

        const payerNodes = [];
        const seenPayerIdsForFullTree = new Set(); // 新增：用于在full tree构建中去重

        uniquePayerIds.forEach(payerId => {
          if (seenPayerIdsForFullTree.has(payerId)) return; // 避免重复添加
          seenPayerIdsForFullTree.add(payerId);

          const payer = this.payersMap.get(payerId);
          if (!payer) return;

          const payerKey = `${agentRecord.agent_id}_${payerId}`;
          let links = this.linksByPayerMap.get(payerKey) || [];

          if (this.formInline.link_id) {
            links = links.filter(link => link.link_id === this.formInline.link_id);
          }

          if (links.length === 0) return;
          const payerNodeId = `payer_${agentRecord.recordId}_${payerId}`;

          // 创建payer节点
          const payerNode = {
            id: payerNodeId,
            label: `${payer.name} (${payer.payer_id})`,
            type: 'payer',
            payer_id: payerId,
            agent_id: agentRecord.agent_id,
            recordId: agentRecord.recordId,
            isLeaf: false,
            children: links.map(link => {
              const customerName = link.customer_name || link.name || `客户${link.link_id}`;
              return {
                id: `link_${agentRecord.agent_id}_${payerId}_${link.link_id}`,
                label: `${customerName} (LinkID: ${link.link_id})`,
                type: 'link',
                link_id: link.link_id,
                payer_id: payerId,
                agent_id: agentRecord.agent_id,
                customer_name: customerName,
                isLeaf: true
              };
            })
          };

          payerNodes.push(payerNode);
        });

        if (payerNodes.length === 0) return;

        const agentNodeId = `agent_${agentRecord.recordId}`;

        const agentNode = {
          id: agentNodeId,
          label: `${agentRecord.company} (${agentRecord.agent_id})`,
          type: 'agent',
          agent_id: agentRecord.agent_id,
          recordId: agentRecord.recordId,
          payers: agentRecord.payers,
          isLeaf: false,
          children: payerNodes
        };

        treeData.push(agentNode);

        // 只有在payer_id或link_id有筛选条件时才自动展开匹配的节点
        if (this.formInline.selected_agent_record_id || this.formInline.payer_id || this.formInline.link_id) {
          expandedKeys.push(agentNodeId);
          // 如果是按伙伴搜索，只展开一级节点（agent节点），不展开其下的payer节点
          if (this.formInline.selected_agent_record_id && !this.formInline.payer_id && !this.formInline.link_id) {
            // 此时只展开agent节点，不展开payer节点
          } else if (this.formInline.payer_id || this.formInline.link_id) {
            // 如果是按payer或link搜索，则展开agent和payer节点
            payerNodes.forEach(payerNode => {
              expandedKeys.push(payerNode.id);
            });
          }
        }
      });

      this.entityTreeData = treeData;
      this.defaultExpandedKeys = expandedKeys;

      // 通过defaultExpandedKeys设置展开状态
      this.$nextTick(() => {
        if (this.$refs.entityTree && expandedKeys.length > 0) {
          // 强制更新组件以应用新的defaultExpandedKeys
          // this.$refs.entityTree.$forceUpdate(); // 移除此行，避免不必要的强制更新，Vue 3通常会自动响应defaultExpandedKeys的变化
          // 对于懒加载树，需要手动展开节点
          expandedKeys.forEach(key => {
            const node = this.$refs.entityTree.getNode(key);
            if (node && !node.expanded) {
              node.expand();
            }
          });
        }
      });
    },

    // 处理账单类型选择变化
    handleBillTypeChange() {
      // 账单类型变化时，如果已选中树节点，则重新触发该节点的计算和图表更新
      if (this.selectedTreeNode) {
        this.handleTreeNodeClick(this.selectedTreeNode);
      }
      this.filterEntityTree();
    },

    // 处理销售选择变化
    handleSalesChange() {
      // 清空其他筛选条件
      this.formInline.selected_agent_record_id = '';
      this.formInline.payer_id = '';
      this.formInline.link_id = '';
      this.filterEntityTree();
    },

    // 处理伙伴选择变化
    handleAgentChange() {
      // 清空payer和link的筛选
      this.formInline.payer_id = '';
      this.formInline.link_id = '';
      this.filterEntityTree();
    },

    // 处理Payer选择变化
    handlePayerChange() {
      this.formInline.link_id = ''; // 清空link筛选
      this.filterEntityTree();
    },

    // 处理Link选择变化
    handleLinkChange() {
      this.filterEntityTree();
    },

    // 判断并设置当前货币
    async determineCurrency(node) {
      let region = null;
      let isMixed = false;

      if (node.type === 'link') {
        // Link节点：通过payer_id查找region
        const payer = this.payersMap.get(node.payer_id);
        region = payer?.region || null;
        
      } else if (node.type === 'payer') {
        // Payer节点：直接从payersMap获取region
        const payer = this.payersMap.get(node.payer_id);
        region = payer?.region || null;
        
      } else if (node.type === 'agent') {
        // Agent节点：检查该agent下所有payer的region
        const agentRecord = this.agentRecordsMap.get(node.recordId);
        if (agentRecord && agentRecord.payers) {
          const payerIds = agentRecord.payers.split(',').map(id => id.trim()).filter(id => id);
          const regions = new Set();
          
          payerIds.forEach(payerId => {
            const payer = this.payersMap.get(payerId);
            if (payer && payer.region) {
              regions.add(payer.region);
            }
          });
          
          // 如果包含多个region，标记为混合
          if (regions.size > 1) {
            isMixed = true;
            region = 'mixed';
          } else if (regions.size === 1) {
            region = Array.from(regions)[0];
          }
        }
      }

      // 根据region设置货币信息
      this.setCurrency(region, isMixed);
      
      console.log('当前货币设置:', this.currentCurrency);
    },

    // 设置货币信息
    setCurrency(region, isMixed = false) {
      if (isMixed) {
        // 混合货币：默认显示美元（因为需要统一单位）
        this.currentCurrency = {
          symbol: '$',
          code: 'USD',
          region: 'mixed',
          isMixed: true
        };
        ElMessage.warning('该节点包含多个区域，金额已统一转换为美元显示');
      } else if (region === 'cn') {
        // 中国区：人民币
        this.currentCurrency = {
          symbol: '¥',
          code: 'CNY',
          region: 'cn',
          isMixed: false
        };
      } else if (region === 'global') {
        // 全球区：美元
        this.currentCurrency = {
          symbol: '$',
          code: 'USD',
          region: 'global',
          isMixed: false
        };
      } else {
        // 默认：美元
        this.currentCurrency = {
          symbol: '$',
          code: 'USD',
          region: null,
          isMixed: false
        };
      }
    },

    // 切换统计数据类型（仅影响当月统计饼图）
    async switchStatData(type) {
      console.log("Switching to:", type);
      this.selectedStatType = type;
      // 重新加载饼图数据
      await this.updatePieDataFromStats();
      this.$nextTick(() => {
        this.updatePieChart();
      });
    },

    // 初始化图表
    initChart() {
      this.$nextTick(() => {
        const chartDom = document.getElementById("billChart");
        if (chartDom) {
          // 如果已存在实例，先销毁
          if (this.chartInstance) {
            this.chartInstance.dispose();
          }
          this.chartInstance = echarts.init(chartDom);
          this.updateChart();

          // 添加点击事件监听器
          this.chartInstance.on("click", this.handleChartClick);

          // 添加图例点击事件监听器
          this.chartInstance.on(
              "legendselectchanged",
              this.handleLegendSelectChanged
          );

          // 监听窗口大小变化
          window.addEventListener("resize", this.handleResize);
        }
      });
    },

    // 更新图表
    updateChart() {
      if (!this.chartInstance) return;

      // 安全检查数据
      if (
          !this.chartData ||
          !this.chartData.months ||
          !this.chartData.webDirectData ||
          !this.chartData.supportData
      ) {
        console.warn("图表数据不完整，显示空图表");
        const emptyOption = {
          title: {
            text: '暂无数据',
            left: 'center',
            top: 'middle',
            textStyle: { color: '#999', fontSize: 14 }
          },
          xAxis: { type: 'category', data: [] },
          yAxis: { type: 'value' },
          series: []
        };
        this.chartInstance.setOption(emptyOption, true);
        return;
      }

      // 确保数据数组存在且有效，并过滤无效数据
      const months = (this.chartData.months || []).filter(
          (item) => item != null
      );
      const webDirectData = (this.chartData.webDirectData || []).map((item) => {
        const num = Number(item);
        return isNaN(num) ? 0 : num;
      });
      const supportData = (this.chartData.supportData || []).map((item) => {
        const num = Number(item);
        return isNaN(num) ? 0 : num;
      });

      if (months.length === 0) {
        console.warn("图表数据为空");
        return;
      }

      const series = [];
      
      if (this.monthlyChartVisibility.charge) {
        series.push({
          name: "Charge",
          type: "bar",
          data: webDirectData,
          itemStyle: {
            color: "#409EFF",
            borderRadius: [4, 4, 0, 0],
          },
          barWidth: 30,
          label: {
            show: true,
            position: "top",
            fontSize: 10,
            color: "#1890ff",
            fontWeight: "bold",
          },
        });
      }
      
      if (this.monthlyChartVisibility.totalCost) {
        series.push({
          name: "实际花费",
          type: "bar",
          data: supportData,
          itemStyle: {
            color: "#ff7f50",
            borderRadius: [4, 4, 0, 0],
          },
          barWidth: 30,
          label: {
            show: true,
            position: "top",
            fontSize: 10,
            color: "#ff7f50",
            fontWeight: "bold",
          },
        });
      }

      const option = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          backgroundColor: "rgba(50, 50, 50, 0.9)",
          borderColor: "transparent",
          textStyle: {
            color: "#fff",
            fontSize: 12,
          },
        },
        legend: {
          show: true,
          top: 10,
          left: "center",
          itemWidth: 14,
          itemHeight: 14,
          textStyle: {
            color: "#666",
            fontSize: 12,
          },
          data: series.map(s => s.name),
          selectedMode: false,
        },
        grid: {
          left: "8%",
          right: "4%",
          bottom: "0%",
          top: "10%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: months,
          axisLabel: {
            color: "#666",
            fontSize: 11,
          },
          axisLine: {
            lineStyle: {
              color: "#e0e0e0",
            },
          },
          axisTick: {
            show: false,
          },
        },
        yAxis: {
          type: "value",
          axisLabel: {
            color: "#666",
            fontSize: 11,
            formatter: `${this.currencySymbol}{value}`,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            lineStyle: {
              color: "#f0f0f0",
              type: "dashed",
            },
          },
        },
        series: series,
      };

      try {
        this.chartInstance.clear();
        this.chartInstance.setOption(option, true);
        console.log("Chart updated successfully");
      } catch (e) {
        console.error("设置图表配置时出错:", e);
        // 如果出错，重新初始化图表
        this.chartInstance.dispose();
        this.chartInstance = null;
        this.$nextTick(() => {
          this.initChart();
        });
      }
    },

    // 处理Tab切换
    handleTabClick(tab) {
      console.log("Tab clicked:", tab.props.name);
    },

    // 优化：处理树节点点击，支持父节点聚合查询
    async handleTreeNodeClick(data) {
      console.log("Tree node clicked:", data);
      console.log("Node type:", data.type);

      // 关闭所有现有的消息提示
      ElMessage.closeAll();

      // 判断并设置当前货币
      await this.determineCurrency(data);

      /*
       * 优化说明：
       * 1. 对于agent节点：只传递agent_id，后端聚合该agent下所有payer和link的数据
       * 2. 对于payer节点：传递agent_id和payer_id，后端聚合该payer下所有link的数据
       * 3. 对于link节点：传递完整参数，查询单个link的数据
       * 这样可以大幅减少请求数量，从原来的N个link请求优化为1个聚合请求
       */

      // 设置选中的节点信息
      this.selectedTreeNode = {
        id: data.id,
        label: data.label,
        type: data.type,
        agent_id: data.agent_id,
        payer_id: data.payer_id,
        link_id: data.link_id,
        sales: data.sales,
        // 构建完整路径
        path: this.getNodePath(data),
      };

      // 计算节点数据
      const nodeData = await this.calculateNodeData(data);
      console.log("计算得到的节点数据:", nodeData);

      // 更新图表数据
      this.updateChartsWithNodeData(nodeData);

      // 根据节点类型加载对应的账单数据
      this.loadBillDataForNode(data);

      if (data.type === 'link') {
        ElMessage.success(`选择了客户: ${data.label}`);
      } else {
        ElMessage.info(`选择了: ${data.label}`);
      }

      console.log("Current selectedTreeNode:", this.selectedTreeNode);
    },

    // 获取节点完整路径
    getNodePath(targetNode) {
      const path = [];
      const findPath = (nodes, target, currentPath = []) => {
        for (const node of nodes) {
          const newPath = [...currentPath, node.label];
          if (node.id === target.id) {
            path.push(...newPath);
            return true;
          }
          if (node.children && findPath(node.children, target, newPath)) {
            return true;
          }
        }
        return false;
      };
      findPath(this.entityTreeData, targetNode);
      return path.join(" > ");
    },

    // 根据节点加载账单数据
    loadBillDataForNode(node) {
      // 这里可以根据节点类型和ID调用相应的API获取账单数据
      console.log('加载账单数据:', node);

      // 示例：根据不同节点类型调用不同的API
      if (node.type === 'agent') {
        // 加载agent级别的账单数据
        this.loadAgentBillData(node.agent_id);
      } else if (node.type === 'payer') {
        // 加载payer级别的账单数据
        this.loadPayerBillData(node.agent_id, node.payer_id);
      } else if (node.type === 'link') {
        // 加载link级别的账单数据
        this.loadLinkBillData(node.agent_id, node.payer_id, node.link_id);
      }
      // 无论何种节点类型，都更新图表数据
      this.updateChartsData();
    },

    // 优化：计算节点数据，支持父节点直接聚合查询
    async calculateNodeData(node) {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      const monthlyData = [];

      console.log("开始计算节点数据:", node);

      // 并行请求所有月份数据
      const monthPromises = [];
      for (let month = 1; month <= currentMonth; month++) {
        const monthStr = `${currentYear}${month.toString().padStart(2, '0')}`;
        
        let promise;
        if (node.type === 'link') {
          promise = this.loadAggregatedBillData(node.agent_id, node.payer_id, monthStr, node.link_id);
        } else if (node.type === 'payer') {
          promise = this.loadAggregatedBillData(node.agent_id, node.payer_id, monthStr);
        } else if (node.type === 'agent') {
          promise = this.loadAggregatedBillData(node.agent_id, null, monthStr);
        }
        
        monthPromises.push(promise.then(data => ({ month, data })));
      }

      // 等待所有请求完成
      const results = await Promise.all(monthPromises);
      
      let totalCharge = 0;
      let totalCost = 0;

      results.forEach(({ month, data }) => {
        const monthCharge = data.totalCharge || 0;
        const monthCost = data.totalCost || 0;
        
        monthlyData.push({
          month: `${currentYear}-${month.toString().padStart(2, '0')}`,
          charge: monthCharge,
          totalCost: monthCost,
          growthRate: month > 1 ? this.calculateGrowthRate(monthlyData[month - 2]?.totalCost || 0, monthCost) : 0
        });

        totalCharge += monthCharge;
        totalCost += monthCost;
      });

      console.log("节点数据计算完成:", { totalCharge, totalCost, monthlyData });

      return { totalCharge, totalCost, monthlyData };
    },

    // 获取节点下的所有link（保留用于兼容性）
    getLinksForNode(node) {
      const links = []
      if (node.type === 'link') {
        links.push({
          link_id: node.link_id,
          payer_id: node.payer_id,
          agent_id: node.agent_id
        });
      } else if (node.type === 'payer') {
        const payerKey = `${node.agent_id}_${node.payer_id}`;
        const payerLinks = this.linksByPayerMap.get(payerKey) || [];
        links.push(...payerLinks);
      } else if (node.type === 'agent') {
        const agentRecord = this.agentRecordsMap.get(node.recordId);
        if (agentRecord && agentRecord.payers) {
          const payerIds = agentRecord.payers.split(',').map(id => id.trim()).filter(id => id);
          payerIds.forEach(payerId => {
            const payerKey = `${node.agent_id}_${payerId}`;
            const payerLinks = this.linksByPayerMap.get(payerKey) || [];
            links.push(...payerLinks);
          });
        }
      }

      return links;
    },



    // 优化：批量加载账单数据，支持父节点聚合查询
    async loadBatchLinkBillData(links, year, month) {
      const monthStr = `${year}${month.toString().padStart(2, '0')}`;
      console.log(`批量计算 ${links.length} 个Link 在 ${monthStr} 的数据...`);

      // 按agent和payer分组，减少请求数量
      const groupedRequests = this.groupLinksByAgentAndPayer(links);
      const aggregatedData = {};

      // 并行处理每个分组
      await Promise.all(Object.keys(groupedRequests).map(async (key) => {
        const group = groupedRequests[key];
        const [agentId, payerId] = key.split('_');

        try {
          // 对于父节点，只传递agentId或payerId，让后端聚合所有子节点数据
          const billData = await this.loadAggregatedBillData(agentId, payerId, monthStr);

          // 将聚合数据分配给该组下的所有links
          group.links.forEach(link => {
            aggregatedData[link.link_id] = {
              charge: billData.totalCharge / group.links.length, // 平均分配
              totalCost: billData.totalCost / group.links.length
            };
          });
        } catch (error) {
          console.error(`获取分组 ${key} 的账单数据失败:`, error);
          // 失败时为该组所有links设置默认值
          group.links.forEach(link => {
            aggregatedData[link.link_id] = { charge: 0, totalCost: 0 };
          });
        }
      }));

      return aggregatedData;
    },

    // 按agent和payer对links进行分组
    groupLinksByAgentAndPayer(links) {
      const groups = {};
      links.forEach(link => {
        const key = `${link.agent_id}_${link.payer_id}`;
        if (!groups[key]) {
          groups[key] = {
            agentId: link.agent_id,
            payerId: link.payer_id,
            links: []
          };
        }
        groups[key].links.push(link);
      });
      return groups;
    },

    // 加载聚合账单数据（父节点优化）
    async loadAggregatedBillData(agentId, payerId, month, linkId = null) {
      try {
        // 构建参数，优先使用父节点ID进行聚合查询
        const params = {
          month: month,
          billType: this.formInline.billType,
          page: 1,
          limit: 999999
        };

        // 只传递父节点参数，让后端聚合子节点数据
        if (agentId) {
          params.agent_id = agentId;
        }
        if (payerId) {
          params.payer_id = payerId;
        }
        if (linkId) {
          params.link_id = linkId;
        }

        // 添加超时保护
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('请求超时')), 30000);
        });
        
        const res = await Promise.race([getBillList(params), timeoutPromise]);
        if (!res || typeof res !== 'object') {
          console.error('响应数据格式错误');
          return { totalCharge: 0, totalCost: 0, recordCount: 0 };
        }
        
        if (res.code === 2000) {
          let billData = [];
          if (res.data && Array.isArray(res.data.data)) {
            billData = res.data.data;
          } else if (Array.isArray(res.data)) {
            billData = res.data;
          }

          // 聚合计算总charge和totalCost
          let totalCharge = 0;
          let totalCost = 0;

          billData.forEach(bill => {
            let charge = Number(bill.charge) || 0;
            let cost = Number(bill.costTotal) || 0;
            
            // 根据当前货币设置决定是否转换
            if (this.currentCurrency.isMixed || this.currentCurrency.region === 'global') {
              // 如果是混合货币或全球区，需要将CN区的金额转换为USD
              const billPayerId = bill.payerId || bill.payer_id;
              const payer = this.payersMap.get(billPayerId);
              if (payer && payer.region === 'cn') {
                charge = charge / this.exchangeRate;
                cost = cost / this.exchangeRate;
              }
            }
            // 如果当前是CN区，不需要转换，保持原始人民币金额
            
            totalCharge += charge;
            totalCost += cost;
          });

          return {
            totalCharge,
            totalCost,
            recordCount: billData.length
          };
        } else {
          console.warn("获取聚合账单数据失败:", res);
          return { totalCharge: 0, totalCost: 0, recordCount: 0 };
        }
      } catch (error) {
        console.error("加载聚合账单数据异常:", error);
        return { totalCharge: 0, totalCost: 0, recordCount: 0 };
      }
    },



    // 计算增长率
    calculateGrowthRate(previousValue, currentValue) {
      if (previousValue === 0) return 0;
      return ((currentValue - previousValue) / previousValue * 100).toFixed(2);
    },

    // 使用节点数据更新图表
    async updateChartsWithNodeData(nodeData) {
      console.log('updateChartsWithNodeData: 开始更新图表数据', nodeData);
      
      // 更新月度统计图表数据
      const months = nodeData.monthlyData.map(item => item.month);
      const chargeData = nodeData.monthlyData.map(item => parseFloat(item.charge.toFixed(2)));
      const totalCostData = nodeData.monthlyData.map(item => parseFloat(item.totalCost.toFixed(2)));

      console.log('图表数据:', { months, chargeData, totalCostData });

      // 更新chartData
      this.chartData = {
        months: months,
        webDirectData: chargeData,
        supportData: totalCostData
      };

      // 更新当前统计数据
      const currentMonthIndex = new Date().getMonth();
      const currentMonthData = nodeData.monthlyData[currentMonthIndex];
      if (currentMonthData) {
        this.statsData.webdirect.webdirect = currentMonthData.charge.toFixed(2);
        this.statsData.webdirect.package = currentMonthData.totalCost.toFixed(2);
        this.statsData.package.webdirect = currentMonthData.charge.toFixed(2);
        this.statsData.package.package = currentMonthData.totalCost.toFixed(2);
      } else {
        this.statsData.webdirect.webdirect = "0.00";
        this.statsData.webdirect.package = "0.00";
        this.statsData.package.webdirect = "0.00";
        this.statsData.package.package = "0.00";
      }

      // 更新饼图数据
      await this.updatePieDataFromStats();

      // 更新折线图数据
      this.updateLineChartWithGrowthRate(nodeData.monthlyData);

      // 强制更新图表
      this.$nextTick(() => {
        console.log('开始更新图表显示');
        this.updateChart();
        this.updatePieChart();
        this.updateLineChart();
      });
    },

    // 根据统计数据更新饼图数据
    async updatePieDataFromStats() {
      if (!this.selectedTreeNode) {
        // 没有选中节点时，清空饼图数据
        this.statsData.webdirect.pieData = [];
        this.statsData.package.pieData = [];
        return;
      }

      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      const monthStr = `${currentYear}${currentMonth.toString().padStart(2, '0')}`;

      try {
        let pieData = [];
        
        if (this.selectedTreeNode.type === 'link') {
          // link节点：显示每个productName的占比
          pieData = await this.loadProductPieData(this.selectedTreeNode.link_id, monthStr);
        } else if (this.selectedTreeNode.type === 'payer') {
          // payer节点：显示每个link的占比
          pieData = await this.loadLinkPieData(this.selectedTreeNode.agent_id, this.selectedTreeNode.payer_id, monthStr);
        } else if (this.selectedTreeNode.type === 'agent') {
          // agent节点：显示每个payer的占比
          pieData = await this.loadPayerPieData(this.selectedTreeNode.agent_id, monthStr);
        }

        // 根据选中的统计类型设置饼图数据，过滤掉值为0的项
        if (this.selectedStatType === 'webdirect') {
          this.statsData.webdirect.pieData = pieData
            .filter(item => item.charge > 0)
            .map(item => ({
              name: item.name,
              value: item.charge,
              itemStyle: { color: this.getRandomColor() }
            }));
        } else {
          this.statsData.package.pieData = pieData
            .filter(item => item.costTotal > 0)
            .map(item => ({
              name: item.name,
              value: item.costTotal,
              itemStyle: { color: this.getRandomColor() }
            }));
        }
      } catch (error) {
        console.error('更新饼图数据失败:', error);
        this.statsData.webdirect.pieData = [];
        this.statsData.package.pieData = [];
      }
    },

    // 加载产品名称的饼图数据（link节点）
    async loadProductPieData(linkId, month) {
      try {
        const params = {
          link_id: linkId,
          month: month
        };
        const res = await getProductAggregation(params);
        
        if (res.code === 2000 && res.data) {
          return res.data.map(item => ({
            name: item.productName,
            charge: item.charge,
            costTotal: item.costTotal
          }));
        }
        return [];
      } catch (error) {
        console.error('加载产品饼图数据失败:', error);
        return [];
      }
    },

    // 加载link的饼图数据（payer节点）
    async loadLinkPieData(agentId, payerId, month) {
      try {
        const params = {
          agent_id: agentId,
          payer_id: payerId,
          month: month,
          page: 1,
          limit: 999999
        };
        const res = await getBillList(params);
        
        if (res.code === 2000 && res.data) {
          const billData = Array.isArray(res.data.data) ? res.data.data : res.data;
          
          // 按linkId聚合
          const linkMap = {};
          billData.forEach(bill => {
            const linkId = bill.linkId || bill.link_id || '未知Link';
            if (!linkMap[linkId]) {
              linkMap[linkId] = {
                name: `Link ${linkId}`,
                charge: 0,
                costTotal: 0
              };
            }
            linkMap[linkId].charge += Number(bill.charge) || 0;
            linkMap[linkId].costTotal += Number(bill.costTotal) || 0;
          });
          
          return Object.values(linkMap);
        }
        return [];
      } catch (error) {
        console.error('加载Link饼图数据失败:', error);
        return [];
      }
    },

    // 加载payer的饼图数据（agent节点）
    async loadPayerPieData(agentId, month) {
      try {
        const params = {
          agent_id: agentId,
          month: month,
          page: 1,
          limit: 999999
        };
        const res = await getBillList(params);
        
        if (res.code === 2000 && res.data) {
          const billData = Array.isArray(res.data.data) ? res.data.data : res.data;
          
          // 按payerId聚合
          const payerMap = {};
          billData.forEach(bill => {
            const payerId = bill.payerId || bill.payer_id || '未知Payer';
            if (!payerMap[payerId]) {
              payerMap[payerId] = {
                name: `Payer ${payerId}`,
                charge: 0,
                costTotal: 0
              };
            }
            payerMap[payerId].charge += Number(bill.charge) || 0;
            payerMap[payerId].costTotal += Number(bill.costTotal) || 0;
          });
          
          return Object.values(payerMap);
        }
        return [];
      } catch (error) {
        console.error('加载Payer饼图数据失败:', error);
        return [];
      }
    },

    // 生成随机颜色
    getRandomColor() {
      const colors = ['#5ca3db', '#70AD47', '#f7941d', '#ee6c6c', '#9f7aea', '#48c9b0', '#f39c12', '#e74c3c', '#3498db', '#9b59b6'];
      return colors[Math.floor(Math.random() * colors.length)];
    },

    // 更新折线图数据并添加增长率
    updateLineChartWithGrowthRate(monthlyData) {
      // 准备折线图数据，只显示有数据的月份
      const validData = monthlyData.filter(item => item.charge > 0 || item.totalCost > 0);

      this.lineChartData = {
        months: validData.map(item => item.month),
        chargeData: validData.map(item => parseFloat(item.charge.toFixed(2))),
        totalCostData: validData.map(item => parseFloat(item.totalCost.toFixed(2))),
        growthRates: validData.map(item => parseFloat(item.growthRate))
      };
    },

    // 加载Agent级别账单数据（使用聚合查询）
    async loadAgentBillData(agentId) {
      try {
        console.log('加载Agent账单数据:', agentId);
        // 使用聚合查询方法
        // const aggregatedData = await this.loadAggregatedBillData(agentId, null, currentMonth);
      } catch (error) {
        console.error('加载Agent账单数据失败:', error);
      }
    },

    // 加载Payer级别账单数据（使用聚合查询）
    async loadPayerBillData(agentId, payerId) {
      try {
        console.log('加载Payer账单数据:', agentId, payerId);
        // 使用聚合查询方法
        // const aggregatedData = await this.loadAggregatedBillData(agentId, payerId, currentMonth);
      } catch (error) {
        console.error('加载Payer账单数据失败:', error);
      }
    },

    // 优化：加载Link级别账单数据，使用聚合方法保持一致性
    async loadLinkBillData(agentId, payerId, linkId, month) {
      try {
        console.log("加载Link账单数据:", agentId, payerId, linkId, month);
        // 直接使用聚合方法，传入linkId参数
        return await this.loadAggregatedBillData(agentId, payerId, month, linkId);
      } catch (error) {
        console.error("加载Link账单数据异常:", error);
        return { totalCharge: 0, totalCost: 0, recordCount: 0 };
      }
    },

    // 更新图表数据
    updateChartsData() {
      // 更新各个图表的数据
      this.updateChart();
      this.updatePieChart();
      this.updateLineChart();
    },

    // 查看账单详情
    viewBillDetail() {
      console.log("查看账单详情");
      ElMessage.success("跳转到账单详情页面");
      // 这里可以添加路由跳转逻辑
      // this.$router.push({ name: 'billDetail', query: { month: '2025-09' } });
    },

    // 初始化饼图
    initPieChart() {
      this.$nextTick(() => {
        const chartDom = document.getElementById("pieChart");
        if (chartDom) {
          this.pieChartInstance = echarts.init(chartDom);
          this.updatePieChart();
        }
      });
    },

    // 更新饼图 - 根据节点类型显示不同的数据
    updatePieChart() {
      if (!this.pieChartInstance) return;

      // 根据当前选中的节点类型和统计类型获取饼图数据
      const pieData = this.currentPieData || [];
      const seriesName = this.selectedStatType === 'webdirect' ? 'Charge占比' : '实际花费占比';

      // 检查是否所有数据都为0
      const hasData = pieData.some(item => item.value > 0);

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: `{a} <br/>{b} : ${this.currencySymbol}{c} ({d}%)`
        },
        legend: {
          show: false
        },
        series: [
          {
            name: seriesName,
            type: 'pie',
            radius: ['25%', '55%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: hasData,
              position: 'outside',
              formatter: '{b}\n¥{c}\n{d}%',
              fontSize: 11
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 12,
                fontWeight: 'bold'
              }
            },
            data: hasData ? pieData : []
          }
        ]
      };

      try {
        this.pieChartInstance.setOption(option);
      } catch (e) {
        console.error("设置饼图配置时出错:", e);
      }
    },

    // 初始化折线图
    initLineChart() {
      this.$nextTick(() => {
        const chartDom = document.getElementById("lineChart");
        if (chartDom) {
          this.lineChartInstance = echarts.init(chartDom);
          this.updateLineChart();
        }
      });
    },

    // 更新折线图
    updateLineChart() {
      if (!this.lineChartInstance) return;

      // 使用lineChartData如果存在，否则使用默认数据
      const months = this.lineChartData?.months || ["2025-01", "2025-03", "2025-05", "2025-07"];
      const chargeData = this.lineChartData?.chargeData || [256.55, 3196.86, 3452.23, 4211.64];
      const totalCostData = this.lineChartData?.totalCostData || [491.8, 747.41, 3700.82, 3956.23];
      const growthRates = this.lineChartData?.growthRates || [0, 52.1, 395.2, 6.9];

      // 计算显示的折线数量
      const visibleCount = (this.yearlyLineVisibility.charge ? 1 : 0) + (this.yearlyLineVisibility.totalCost ? 1 : 0);
      const showLabel = visibleCount === 1;

      const option = {
        tooltip: {
          trigger: "axis",
          backgroundColor: "rgba(50, 50, 50, 0.9)",
          borderColor: "transparent",
          textStyle: {
            color: "#fff",
            fontSize: 12,
          },
          formatter: function (params) {
            if (!params || params.length === 0) return "";
            try {
              let result = params[0].axisValue + "<br/>";
              const currencySymbol = this.currencySymbol;
              params.forEach((param, index) => {
                if (param && param.seriesName && param.value !== undefined) {
                  result += `<span style="color:${param.color}">●</span> ${param.seriesName}: ${currencySymbol}${param.value}`;
                  // 为实际花费添加增长率信息
                  if (param.seriesName === '实际花费' && growthRates[param.dataIndex] !== undefined) {
                    const rate = growthRates[param.dataIndex];
                    const rateText = rate > 0 ? `+${rate}%` : `${rate}%`;
                    const rateColor = rate > 0 ? '#52c41a' : rate < 0 ? '#ff4d4f' : '#666';
                    result += ` <span style="color:${rateColor}">(${rateText})</span>`;
                  }
                  result += "<br/>";
                }
              });
              return result;
            } catch (e) {
              console.warn("Line chart tooltip formatter error:", e);
              return "";
            }
          },
        },
        legend: {
          show: true,
          top: 5,
          right: 10,
          itemWidth: 12,
          itemHeight: 8,
          textStyle: {
            fontSize: 10,
            color: "#666",
          },
        },
        grid: {
          left: "12%",
          right: "8%",
          bottom: "0%",
          top: "15%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: months,
          axisLabel: {
            fontSize: 10,
            color: "#666",
            margin: 8,
          },
          axisLine: {
            lineStyle: {
              color: "#e0e0e0",
            },
          },
          axisTick: {
            show: false,
          },
        },
        yAxis: {
          type: "value",
          axisLabel: {
            fontSize: 10,
            color: "#666",
            formatter: `${this.currencySymbol}{value}`,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            lineStyle: {
              color: "#f0f0f0",
              type: "dashed",
            },
          },
        },
        series: [
          {
            name: "Charge",
            type: "line",
            data: chargeData,
            smooth: true,
            itemStyle: {
              color: "#1890ff",
              borderWidth: 2,
            },
            lineStyle: {
              color: "#1890ff",
              width: this.yearlyLineVisibility.charge ? 3 : 0,
            },
            symbol: this.yearlyLineVisibility.charge ? "circle" : "none",
            symbolSize: 8,
            emphasis: {
              symbolSize: 12,
              itemStyle: {
                borderColor: "#fff",
                borderWidth: 2,
              },
            },
            label: {
              show: showLabel,
              position: "top",
              fontSize: 10,
              color: "#1890ff",
              fontWeight: "bold",
              formatter: '{c}',
              backgroundColor: "rgba(24, 144, 255, 0.1)",
              padding: [2, 6],
              borderRadius: 4,
            },
          },
          {
            name: "实际花费",
            type: "line",
            data: totalCostData,
            smooth: true,
            itemStyle: {
              color: "#ff7f50",
              borderWidth: 2,
            },
            lineStyle: {
              color: "#ff7f50",
              width: this.yearlyLineVisibility.totalCost ? 3 : 0,
            },
            symbol: this.yearlyLineVisibility.totalCost ? "circle" : "none",
            symbolSize: 8,
            emphasis: {
              symbolSize: 12,
              itemStyle: {
                borderColor: "#fff",
                borderWidth: 2,
              },
            },
            label: {
              show: showLabel,
              position: "top",
              fontSize: 10,
              color: "#ff7f50",
              fontWeight: "bold",
              formatter: '{c}',
              backgroundColor: "rgba(255, 127, 80, 0.1)",
              padding: [2, 6],
              borderRadius: 4,
            },
          },
        ],
      };

      try {
        this.lineChartInstance.setOption(option);
      } catch (e) {
        console.error("设置折线图配置时出错:", e);
      }
    },

    // 处理窗口大小变化
    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize();
      }
      if (this.pieChartInstance) {
        this.pieChartInstance.resize();
      }
      if (this.lineChartInstance) {
        this.lineChartInstance.resize();
      }
      // 只有当resourceChartInstance和tagChartInstance存在时才resize
      if (this.resourceChartInstance) {
        this.resourceChartInstance.resize();
      }
      if (this.tagChartInstance) {
        this.tagChartInstance.resize();
      }
    },

    // 处理柱状图点击事件
    handleChartClick(params) {
      console.log("Chart clicked:", params);
      if (params && params.name && this.selectedTreeNode) {
        // 构建查询参数
        const nodeId = this.selectedTreeNode.type === 'link' ? this.selectedTreeNode.link_id : 
                       this.selectedTreeNode.type === 'payer' ? this.selectedTreeNode.payer_id : 
                       this.selectedTreeNode.agent_id;
        
        // 跳转到账单详情页面（会在系统顶部Tab中新增页面）
        this.$router.push({
          name: "billDetail",
          query: {
            month: params.name,
            nodeType: this.selectedTreeNode.type,
            nodeId: nodeId
          }
        });
      }
    },

    // 处理图例选择变化
    handleLegendSelectChanged(params) {
      console.log("Legend select changed:", params);
      // 图例选择不影响其他组件，由ECharts自动处理显示隐藏
    },

    // 获取当前日期
    getCurrentDate() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },



    // 切换月度统计柱状图显示
    toggleMonthlyBar(type) {
      if (type === 'charge') {
        this.monthlyChartVisibility.charge = !this.monthlyChartVisibility.charge;
      } else if (type === 'totalCost') {
        this.monthlyChartVisibility.totalCost = !this.monthlyChartVisibility.totalCost;
      }
      this.$nextTick(() => {
        this.updateChart();
      });
    },

    // 切换年度统计图表折线显示
    toggleYearlyLine(type) {
      if (type === 'charge') {
        this.yearlyLineVisibility.charge = !this.yearlyLineVisibility.charge;
      } else if (type === 'totalCost') {
        this.yearlyLineVisibility.totalCost = !this.yearlyLineVisibility.totalCost;
      }
      this.$nextTick(() => {
        this.updateLineChart();
      });
    },

    // 处理伙伴粒度变化
    handlePartnerGranularityChange(value) {
      if (value === 'all') {
        this.billSettings.selectedPartner = '';
        this.billSettings.selectedPayer = '';
        this.billSettings.selectedLink = '';
      }
    },

    // 处理批量下载伙伴选择变化
    handleBatchPartnerChange() {
      // 批量下载的伙伴选择不影响其他筛选
    },

    // 处理Invoice校验开关
    handleInvoiceToggle(enabled) {
      if (!enabled) {
        this.invoiceSettings.uploadedFiles = [];
      }
    },

    // 显示Invoice上传对话框
    showInvoiceUploadDialog() {
      console.log('showInvoiceUploadDialog called');
      this.invoiceUploadDialogVisible = true;
      this.invoiceFileList = [];
    },

    // 处理Invoice文件变化
    handleInvoiceFileChange(file, fileList) {
      this.invoiceFileList = fileList;
    },

    // 触发文件选择
    triggerFileSelect() {
      this.$refs.fileInput.click();
    },

    // 处理文件选择
    handleFileSelect(e) {
      const files = Array.from(e.target.files);
      const pdfFiles = [];
      
      files.forEach(file => {
        if (file.name.toLowerCase().endsWith('.pdf')) {
          pdfFiles.push({
            file: file,
            fullPath: file.name
          });
        }
      });
      
      if (pdfFiles.length > 20) {
        ElMessage.warning(`选择了 ${pdfFiles.length} 个PDF文件，超过20个文件限制，仅添加前20个`);
      }
      
      const filesToAdd = pdfFiles.slice(0, 20);
      filesToAdd.forEach(item => {
        this.invoiceFileList.push({
          name: item.file.name,
          fullPath: item.fullPath,
          raw: item.file,
          uid: Date.now() + Math.random()
        });
      });
      
      if (filesToAdd.length > 0) {
        ElMessage.success(`已添加 ${filesToAdd.length} 个PDF文件`);
      }
      
      e.target.value = '';
    },

    // 处理文件夹拖拽
    async handleFolderDrop(e) {
      this.isDragging = false;
      const items = e.dataTransfer.items;
      const files = e.dataTransfer.files;
      
      // 检查是否为文件夹拖拽（通过检查第一个item是否为目录）
      let hasDirectory = false;
      if (items && items.length > 0) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i].webkitGetAsEntry();
          if (item && item.isDirectory) {
            hasDirectory = true;
            break;
          }
        }
      }
      
      // 如果是直接拖拽PDF文件（没有文件夹）
      if (!hasDirectory && files && files.length > 0) {
        this.handleDirectFileDrop(files);
        return;
      }

      const pdfFiles = [];
      
      // 递归读取文件夹中的所有PDF文件，保留路径信息
      const readEntry = async (entry, parentPath = '') => {
        if (entry.isFile) {
          return new Promise((resolve) => {
            entry.file((file) => {
              if (file.name.toLowerCase().endsWith('.pdf')) {
                const fullPath = parentPath ? `${parentPath}/${file.name}` : file.name;
                pdfFiles.push({
                  file: file,
                  fullPath: fullPath
                });
              }
              resolve();
            });
          });
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          return new Promise((resolve) => {
            reader.readEntries(async (entries) => {
              const currentPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;
              for (const subEntry of entries) {
                await readEntry(subEntry, currentPath);
              }
              resolve();
            });
          });
        }
      };

      // 处理所有拖拽项
      for (let i = 0; i < items.length; i++) {
        const item = items[i].webkitGetAsEntry();
        if (item) {
          await readEntry(item, '');
        }
      }

      // 将找到的PDF文件添加到文件列表
      if (pdfFiles.length > 0) {
        if (pdfFiles.length > 20) {
          ElMessage.warning(`识别到 ${pdfFiles.length} 个PDF文件，超过20个文件限制，仅添加前20个`);
        }
        
        const filesToAdd = pdfFiles.slice(0, 20);
        filesToAdd.forEach(item => {
          this.invoiceFileList.push({
            name: item.file.name,
            fullPath: item.fullPath,
            raw: item.file,
            uid: Date.now() + Math.random()
          });
        });
        
        ElMessage.success(`已添加 ${filesToAdd.length} 个PDF文件`);
      } else {
        ElMessage.warning('未找到PDF文件');
      }
    },

    // 处理直接拖拽PDF文件
    handleDirectFileDrop(files) {
      const pdfFiles = [];
      Array.from(files).forEach(file => {
        if (file.name.toLowerCase().endsWith('.pdf')) {
          pdfFiles.push({
            file: file,
            fullPath: file.name
          });
        }
      });
      
      if (pdfFiles.length > 0) {
        if (pdfFiles.length > 20) {
          ElMessage.warning(`识别到 ${pdfFiles.length} 个PDF文件，超过20个文件限制，仅添加前20个`);
        }
        
        const filesToAdd = pdfFiles.slice(0, 20);
        filesToAdd.forEach(item => {
          this.invoiceFileList.push({
            name: item.file.name,
            fullPath: item.fullPath,
            raw: item.file,
            uid: Date.now() + Math.random()
          });
        });
        
        ElMessage.success(`已添加 ${filesToAdd.length} 个PDF文件`);
      } else {
        ElMessage.warning('未找到PDF文件');
      }
    },

    // 移除文件
    removeFile(index) {
      this.invoiceFileList.splice(index, 1);
    },

    // 处理Invoice文件移除
    handleInvoiceFileRemove(file, fileList) {
      this.invoiceFileList = fileList;
    },

    // 提交Invoice上传
    async submitInvoiceUpload() {
      if (this.invoiceFileList.length === 0) {
        ElMessage.warning('请选择文件');
        return;
      }

      const formData = new FormData();
      this.invoiceFileList.forEach(item => {
        formData.append('files', item.raw);
      });
      formData.append('month', this.invoiceMonth);

      this.invoiceUploading = true;
      try {
        const response = await fetch('/api/bill/invoice/upload/', {
          method: 'POST',
          body: formData
        });
        const data = await response.json();
        
        if (data.status === 'success') {
          ElMessage.success(data.message);
          this.invoiceUploadDialogVisible = false;
          this.invoiceFileList = [];
          if (this.$refs.invoiceUploadRef) {
            this.$refs.invoiceUploadRef.clearFiles();
          }
        } else if (data.status === 'partial') {
          ElMessage.warning({
            dangerouslyUseHTMLString: true,
            message: data.message.replace(/\n/g, '<br/>'),
            duration: 8000,
            showClose: true
          });
          if (data.failed_count === this.invoiceFileList.length) {
            // 全部失败，不关闭对话框
          } else {
            // 部分成功，关闭对话框
            this.invoiceUploadDialogVisible = false;
            this.invoiceFileList = [];
            if (this.$refs.invoiceUploadRef) {
              this.$refs.invoiceUploadRef.clearFiles();
            }
          }
        } else {
          ElMessage.error({
            message: data.message || '上传失败',
            duration: 5000,
            showClose: true
          });
        }
      } catch (error) {
        ElMessage.error({
          message: '上传失败: ' + (error.message || '未知错误'),
          duration: 5000,
          showClose: true
        });
      } finally {
        this.invoiceUploading = false;
      }
    },

    // Invoice文件上传前的检查
    beforeInvoiceUpload(file) {
      const isPDF = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const isLt10M = file.size / 1024 / 1024 < 10;

      if (!isPDF) {
        ElMessage.error('只支持 PDF 格式的文件!');
        return false;
      }
      if (!isLt10M) {
        ElMessage.error('文件大小不能超过 10MB!');
        return false;
      }
      return true;
    },

    // LOGO上传前的检查
    beforeLogoUpload(file) {
      if (!this.logoSettings.selectedPartner) {
        ElMessage.error('请先选择伙伴!');
        return false;
      }

      const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'].includes(file.type);
      const isLt5M = file.size / 1024 / 1024 < 5;

      if (!isValidType) {
        ElMessage.error('只支持 JPG、PNG、GIF 格式的图片!');
        return false;
      }
      if (!isLt5M) {
        ElMessage.error('图片大小不能超过 5MB!');
        return false;
      }
      return true;
    },

    // LOGO上传成功
    handleLogoUploadSuccess(response, file) {
      ElMessage.success('LOGO上传成功!');
      this.logoSettings.uploadedLogos.push({
        partnerId: this.logoSettings.selectedPartner,
        name: file.name,
        url: response.url || '',
        uploadTime: new Date().toLocaleString()
      });
    },

    // LOGO上传失败
    handleLogoUploadError(error) {
      ElMessage.error('LOGO上传失败，请重试!');
      console.error('Logo upload error:', error);
    },

    // 下载账单（整合表页面使用）
    async downloadBills() {
      // 验证必填项
      if (!this.integrationForm.date || !this.integrationForm.sales) {
        ElMessage.warning('请填写日期和销售');
        return;
      }

      this.loading = true;
      try {
        const params = this.buildDownloadParams();
        params.billType = 2;
        params.file_format = this.billFormat;
        const res = await downloadBillByUrl(params);
        
        if (res.code === 2000 && res.data) {
          this.downloadFile(res.data);
          ElMessage.success('账单下载成功');
        } else {
          ElMessage.error(res.msg || '下载失败');
        }
      } catch (error) {
        console.error('下载账单失败:', error);
        ElMessage.error('下载失败，请重试');
      } finally {
        this.loading = false;
      }
    },

    // 下载对账单（整合表页面使用）
    async downloadReconciliation() {
      // 验证必填项
      if (!this.integrationForm.date || !this.integrationForm.sales) {
        ElMessage.warning('请填写日期和销售');
        return;
      }

      this.loading = true;
      try {
        const params = this.buildDownloadParams();
        params.billType = 2;
        const res = await downloadBillDetail(params);
        
        if (res.code === 2000 && res.data) {
          this.downloadFile(res.data);
          ElMessage.success('对账单下载成功');
        } else {
          ElMessage.error(res.msg || '下载失败');
        }
      } catch (error) {
        console.error('下载对账单失败:', error);
        ElMessage.error('下载失败，请重试');
      } finally {
        this.loading = false;
      }
    },

    // 下载Invoice PDF（整合表页面使用）
    async downloadInvoicePDF() {
      // 验证必填项
      if (!this.integrationForm.date || !this.integrationForm.sales) {
        ElMessage.warning('请填写日期和销售');
        return;
      }

      this.loading = true;
      try {
        // 构建下载参数
        const month = this.integrationForm.date.replace('-', '');
        const params = {
          month: month,
          sales: this.integrationForm.sales
        };

        // 添加可选筛选条件
        if (this.integrationForm.partner) {
          const agent = this.agentsListWithRecordId.find(a => a.recordId === this.integrationForm.partner);
          if (agent) {
            params.agent_id = agent.agent_id;
          }
        }
        if (this.integrationForm.payer) {
          params.payer_id = this.integrationForm.payer;
        }
        if (this.integrationForm.type && this.integrationForm.type !== 'all') {
          params.region = this.integrationForm.type;
        }
        if (this.integrationForm.link) {
          params.link_id = this.integrationForm.link;
        }
        if (this.integrationForm.account_type) {
          params.account_type = this.integrationForm.account_type;
        }

        console.log('[下载Invoice PDF] 请求参数:', params);

        // 调用后端API
        const response = await fetch('/api/bill/invoice-pdf/download/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params)
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        // 检查响应类型
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          // JSON响应，可能是错误信息
          const data = await response.json();
          if (data.code !== 200) {
            ElMessage.error(data.msg || '下载失败');
            return;
          }
        }

        // 下载文件
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // 从响应头获取文件名
        const disposition = response.headers.get('content-disposition');
        let filename = `Invoice_PDF_${month}.zip`;
        if (disposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
          if (matches && matches[1]) {
            filename = decodeURIComponent(matches[1].replace(/['"]/g, ''));
          }
        }
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        ElMessage.success('Invoice PDF下载成功');
      } catch (error) {
        console.error('下载Invoice PDF失败:', error);
        ElMessage.error('下载失败，请重试');
      } finally {
        this.loading = false;
      }
    },

    // 构建下载参数（基于整合表筛选框）
    buildDownloadParams() {
      const params = {
        billType: this.formInline.billType,
        page: 1,
        limit: 999999
      };

      // 日期：YYYY-MM → YYYYMM
      if (this.integrationForm.date) {
        params.month = this.integrationForm.date.replace('-', '');
      }

      // 销售
      if (this.integrationForm.sales) {
        params.sales = this.integrationForm.sales;
      }

      // 伙伴：recordId → agent_id
      if (this.integrationForm.partner) {
        const agent = this.agentRecordsMap.get(this.integrationForm.partner);
        if (agent) {
          params.agent_id = agent.agent_id;
        }
      }

      // Payer
      if (this.integrationForm.payer) {
        params.payer_id = this.integrationForm.payer;
      }

      // Link（新增）
      if (this.integrationForm.link) {
        params.link_id = this.integrationForm.link;
      }

      // 地区：all→'', global→'global', cn→'cn'
      if (this.integrationForm.type && this.integrationForm.type !== 'all') {
        params.region = this.integrationForm.type;
      }

      // 对账单版本
      if (this.integrationForm.detailBillType) {
        params.detail_bill_type = this.integrationForm.detailBillType;
      }

      // 账号类型：''=全部, 'bt'=Billing Transfer, 'normal'=普通
      if (this.integrationForm.account_type) {
        params.account_type = this.integrationForm.account_type;
      }

      return params;
    },

    // 下载文件
    downloadFile(url) {
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.href = url;
      a.click();
      setTimeout(() => {
        a.remove();
      }, 1000);
    },

    // 整合表下载相关方法
    // 搜索整合表数据
    async searchIntegrationData() {
      if (!this.integrationForm.date || !this.integrationForm.sales) {
        ElMessage.warning('请填写月份和销售');
        return;
      }

      this.integrationLoading = true;
      try {
        const params = new URLSearchParams({
          month: this.integrationForm.date.replace('-', ''),
          sales: this.integrationForm.sales,
          type: this.integrationForm.type
        });
        
        // 如果选择了伙伴，添加agentid参数
        if (this.integrationForm.partner) {
          const agent = this.agentRecordsMap.get(this.integrationForm.partner);
          if (agent) {
            params.append('agentid', agent.agent_id);
          }
        }
        
        // 如果选择了payer，添加payer参数
        if (this.integrationForm.payer) {
          params.append('payer', this.integrationForm.payer);
        }
        
        // 如果选择了link，添加link参数
        if (this.integrationForm.link) {
          params.append('link', this.integrationForm.link);
        }
        
        // 如果选择了账号类型，添加account_type参数
        if (this.integrationForm.account_type) {
          params.append('account_type', this.integrationForm.account_type);
        }
        
        const response = await fetch(`/api/bill/invoice/data/?${params}`);
        const data = await response.json();
        
        console.log('后端返回的数据:', data);
        if (data.data && data.data.length > 0) {
          console.log('第一条数据示例:', data.data[0]);
        }
        
        if (data.code === 2000) {
          this.integrationTableData = data.data || [];
          this.integrationPagination.total = (data.data || []).length;
          ElMessage.success(`查询完成，共${(data.data || []).length}条记录`);
        } else {
          ElMessage.error(data.message || '查询失败');
          this.integrationTableData = [];
          this.integrationPagination.total = 0;
        }
        
      } catch (error) {
        console.error('查询失败:', error);
        ElMessage.error('查询失败，请重试');
        this.integrationTableData = [];
        this.integrationPagination.total = 0;
      } finally {
        this.integrationLoading = false;
      }
    },

    // 生成模拟整合表数据（字段名匹配后端返回）
    generateMockIntegrationData() {
      const mockData = [];
      for (let i = 1; i <= 50; i++) {
        mockData.push({
          po: `PO${String(i).padStart(6, '0')}`,
          payer_id: `123456789${String(i).padStart(3, '0')}`,
          link_id: `987654321${String(i).padStart(3, '0')}`,
          customer_name: `客户${i}`,
          charges: (Math.random() * 1000).toFixed(2),
          credits: (Math.random() * 100).toFixed(2),
          savings_plan: (Math.random() * 50).toFixed(2),
          bundle: (Math.random() * 20).toFixed(2),
          tax: (Math.random() * 30).toFixed(2),
          vat: '0%',
          distribution: (Math.random() * 40).toFixed(2),
          total: (Math.random() * 800).toFixed(2)
        });
      }
      return mockData;
    },

    // 导出表格数据（下载整合表）
    async exportTableData() {
      if (!this.integrationForm.date || !this.integrationForm.sales) {
        ElMessage.warning('请填写日期和销售');
        return;
      }

      this.integrationLoading = true;
      try {
        const params = new URLSearchParams({
          po: this.integrationForm.date.replace('-', ''),
          sales: this.integrationForm.sales,
          type: this.integrationForm.type
        });
        
        // 如果选择了伙伴，添加agentid参数
        if (this.integrationForm.partner) {
          const agent = this.agentRecordsMap.get(this.integrationForm.partner);
          if (agent) {
            params.append('agentid', agent.agent_id);
          }
        }
        
        // 如果选择了payer，添加payer参数
        if (this.integrationForm.payer) {
          params.append('payer', this.integrationForm.payer);
        }
        
        // 如果选择了link，添加link参数
        if (this.integrationForm.link) {
          params.append('link', this.integrationForm.link);
        }
        
        // 如果选择了账号类型，添加account_type参数
        if (this.integrationForm.account_type) {
          params.append('account_type', this.integrationForm.account_type);
        }
        
        const response = await fetch(`/api/bill/invoice/export/?${params}`);
        const blob = await response.blob();
        
        // 根据响应类型决定文件名
        const contentType = response.headers.get('content-type');
        let filename;
        if (contentType.includes('zip')) {
          filename = `export_${this.integrationForm.date}_${this.integrationForm.sales}_${Date.now()}.zip`;
        } else {
          filename = `export_${this.integrationForm.date}_${this.integrationForm.sales}_${Date.now()}.xlsx`;
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
        
        ElMessage.success('导出成功');
      } catch (error) {
        console.error('导出失败:', error);
        ElMessage.error('导出失败，请重试');
      } finally {
        this.integrationLoading = false;
      }
    },

    // 分页相关方法
    handleSizeChange(val) {
      this.integrationPagination.pageSize = val;
      this.searchIntegrationData();
    },

    handleCurrentChange(val) {
      this.integrationPagination.currentPage = val;
      this.searchIntegrationData();
    },

    // 列设置相关方法
    handleColumnSettingsClose() {
      this.showColumnSettings = false;
    },

    applyColumnSettings() {
      this.showColumnSettings = false;
      ElMessage.success('列设置已应用');
    },

    selectAllColumns() {
      this.columnSettings.forEach(col => {
        col.visible = true;
      });
    },

    clearAllColumns() {
      this.columnSettings.forEach(col => {
        col.visible = false;
      });
    },

    resetDefaultColumns() {
      this.columnSettings.forEach(col => {
        col.visible = true;
      });
    },

    onColumnDragEnd() {
      // 拖拽结束后的处理
      console.log('Column order changed');
    },

    // 格式化列值
    formatColumnValue(row, column) {
      const value = row[column.prop];
      
      // vat字段直接显示，后端已经转换为百分比
      if (column.formatter === 'calculateDiscountPercent') {
        return value || '0.00%';
      }

      // 移除括号和货币符号（Savings Plan、Bundle、Discount）
      if (column.formatter === 'removeBrackets') {
        return this.removeBracketsAndCurrency(value);
      }

      if (!value || value === '-') {
        return '-';
      }

      // 移除货币符号
      return this.removeCurrencySymbol(value);
    },

    // 移除括号和货币符号
    removeBracketsAndCurrency(value) {
      if (!value || value === '-' || value === '') return value || '-';
      // 移除括号和货币符号(包括USD)，保留数字和逗号，例如: ($2,692.10) -> 2,692.10
      return String(value).replace(/[()\uff08\uff09$¥€£]|USD/gi, '').trim();
    },

    // 移除货币符号
    removeCurrencySymbol(value) {
      if (!value || value === '-' || value === '') return value || '-';
      // 移除货币符号(包括USD)，保留数字、逗号和括号
      return String(value).replace(/[$¥€£]|USD/gi, '').trim();
    },

    // 提取数字（用于计算）
    extractNumber(value) {
      if (!value || value === '-' || value === '') return 0;
      // 移除所有非数字字符（除了小数点和负号）
      const str = String(value).replace(/[^0-9.-]/g, '').trim();
      const num = parseFloat(str);
      return isNaN(num) ? 0 : num;
    },

    // 处理查询按钮点击
    handleQuery() {
      console.log('查询按钮被点击');

      // 触发实体树的筛选和展开
      this.filterEntityTree();

      // 如果有筛选条件，自动展开相关节点
      this.$nextTick(() => {
        if (this.$refs.entityTree && this.defaultExpandedKeys.length > 0) {
          // 展开所有匹配的节点
          this.defaultExpandedKeys.forEach(key => {
            const node = this.$refs.entityTree.getNode(key);
            if (node && !node.expanded) {
              node.expand();
            }
          });
        }
      });

      ElMessage.success('查询完成');
    },

    // 整合表Link筛选相关方法
    integrationLinkFilterMethod(query) {
      if (this.integrationLinkFilterTimer) {
        clearTimeout(this.integrationLinkFilterTimer);
      }
      this.integrationLinkFilterTimer = setTimeout(() => {
        this.performIntegrationLinkFilter(query);
      }, 150);
    },

    performIntegrationLinkFilter(query) {
      if (!query || query.trim() === '') {
        this.displayedIntegrationLinksList = this.filteredIntegrationLinksList.slice(0, 50);
        return;
      }
      const searchQuery = String(query).trim();
      this.displayedIntegrationLinksList = this.linksList.filter(item => {
        const linkIdStr = String(item.link_id);
        const linkIdMatch = linkIdStr.includes(searchQuery);
        const customerName = item.customer_name || item.name || `客户${item.link_id}`;
        const customerNameMatch = customerName.toLowerCase().includes(searchQuery.toLowerCase());
        return linkIdMatch || customerNameMatch;
      });
      if (this.displayedIntegrationLinksList.length > 50) {
        this.displayedIntegrationLinksList = this.displayedIntegrationLinksList.slice(0, 50);
      }
    },

    // 处理返点核对tab点击
    handleRebateTabClick() {
      this.activeTab = 'rebate';
      // 延迟加载返点核对数据
      this.$nextTick(() => {
        if (this.$refs.checkRebateComponent) {
          this.$refs.checkRebateComponent.permissionData();
          setTimeout(() => {
            this.$refs.checkRebateComponent.getData(true);
          }, 100);
        }
      });
    },

    // 重新生成账单
    regenerateBills() {
      if (!this.integrationForm.date || !this.integrationForm.sales) {
        ElMessage.warning('请填写日期和销售');
        return;
      }

      this.$confirm('确定要重新生成吗？这将重新生成Invoice账单、对账单和整合表。', '确认操作', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.executeRegenerateBills();
      }).catch(() => {
        ElMessage.info('已取消操作');
      });
    },

    // 执行重新生成账单
    async executeRegenerateBills() {
      this.regenerating = true;
      
      // 显示持续的loading提示
      const loadingInstance = ElMessage({
        message: '正在重新生成，请稍候...',
        type: 'info',
        duration: 0,  // 0表示不自动关闭
        showClose: false
      });
      
      try {
        const formData = new FormData();
        formData.append('month', this.integrationForm.date.replace('-', ''));
        formData.append('sales', this.integrationForm.sales);
        
        // 添加可选参数（优先级：link > payer > agent）
        if (this.integrationForm.link) {
          formData.append('link_id', this.integrationForm.link);
        } else if (this.integrationForm.payer) {
          formData.append('payer_id', this.integrationForm.payer);
        } else if (this.integrationForm.partner) {
          const agent = this.agentRecordsMap.get(this.integrationForm.partner);
          if (agent) {
            formData.append('agent_id', agent.agent_id);
          }
        }

        // 账号类型 + 地区：让重新生成范围与查询/下载筛选保持一致
        if (this.integrationForm.account_type) {
          formData.append('account_type', this.integrationForm.account_type);
        }
        if (this.integrationForm.type && this.integrationForm.type !== 'all') {
          formData.append('type', this.integrationForm.type);
        }
        
        const response = await fetch('/api/bill/invoice/data/', {
          method: 'POST',
          body: formData
        });
        const data = await this.parseJsonResponse(response);

        if (data.code === 2000 && data.job_id) {
          // 后台执行，轮询进度（link 多时要几十分钟，不能同步等）
          loadingInstance.close();
          ElMessage.info(`${data.message || '已开始后台重新生成'}，进度见「重新生成」按钮；离开页面不影响后台生成`);
          const job = await this.pollRegenJob(data.job_id, data.total);
          if (job.status === 'done') {
            ElMessage.success(job.message || '重新生成完成');
          } else {
            ElMessage.error(job.message || '重新生成失败');
          }
          return;
        }

        // 关闭loading提示
        loadingInstance.close();

        if (data.code === 2000) {
          ElMessage.success(data.message || '重新生成成功');
        } else {
          ElMessage.error(data.message || '重新生成失败');
        }

      } catch (error) {
        // 关闭loading提示
        loadingInstance.close();
        
        console.error('重新生成错误:', error);
        ElMessage.error('重新生成失败: ' + error.message);
      } finally {
        this.regenerating = false;
        this.regenProgressText = '';
      }
    },

    // 网关超时等情况返回的是 HTML 错误页，直接 response.json() 会报「Unexpected token '<'」
    async parseJsonResponse(response) {
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch (e) {
        throw new Error(`服务器返回异常（HTTP ${response.status}），请稍后在整合表中确认结果`);
      }
    },

    async pollRegenJob(jobId, total) {
      const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
      let errors = 0;
      for (;;) {
        await sleep(5000);
        try {
          const res = await fetch(`/api/bill/invoice/regen-job/?job_id=${jobId}`);
          const data = await this.parseJsonResponse(res);
          if (data.code !== 2000) {
            return { status: 'error', message: data.message || '查询进度失败' };
          }
          errors = 0;
          const job = data.data;
          this.regenProgressText = `生成中 ${job.done}/${job.total || total}`;
          if (job.status !== 'running') return job;
        } catch (e) {
          // 网络抖动时继续轮询，连续失败才放弃（后台任务不受影响）
          if (++errors >= 6) {
            return { status: 'error', message: `查询进度失败：${e.message}；后台仍在生成，稍后刷新查看` };
          }
        }
      }
    },

    // 下载全部
    // 下载全部（整合表 + 账单 + 对账单 + Invoice PDF）
    async downloadAll() {
      if (!this.integrationForm.date || !this.integrationForm.sales) {
        ElMessage.warning('请填写日期和销售');
        return;
      }

      this.loading = true;
      try {
        const params = this.buildDownloadParams();
        params.billType = 2;
        params.file_format = this.billFormat;

        ElMessage.info('正在打包所有文件（整合表、账单、对账单、Invoice PDF），请稍候...');

        const response = await fetch(`/api/bill/downloadAll/?${new URLSearchParams(params)}`);
        const data = await response.json();
        
        if (data.code === 2000 && data.data) {
          this.downloadFile(data.data);
          ElMessage.success('全部文件下载成功（包含Invoice PDF）');
        } else {
          ElMessage.error(data.msg || '下载失败');
        }
      } catch (error) {
        console.error('下载全部失败:', error);
        ElMessage.error('下载失败，请重试');
      } finally {
        this.loading = false;
      }
    }
  },
};
</script>


<style lang="scss" scoped>
.bill-manage-page {
  background: #f8f8fa;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page-container {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* 左侧边栏样式 */
.left-sidebar {
  width: 350px;
  background: #ffffff;
  border-right: 1px solid #e8eaec;

  .sidebar-header {
    padding: 16px 20px;
    border-bottom: 1px solid #e8eaec;
    display: flex;
    align-items: center;
    background: #fafbfc;

    .header-icon {
      color: #409eff;
      margin-right: 8px;
      font-size: 16px;
    }

    .header-title {
      font-size: 14px;
      font-weight: 500;
      color: #303133;
    }
  }

  .filter-section {
    padding: 16px 20px;
    border-bottom: 1px solid #e8eaec;
    background: #ffffff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);

    .filter-row {
      display: flex;
      align-items: center;
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 0;
      }

      .filter-label {
        flex-shrink: 0;
        width: 90px;
        font-size: 13px;
        color: #303133;
        font-weight: 500;
        text-align: right;
        margin-right: 12px;
      }

      .filter-select {
        flex: 1;

        :deep(.el-input__wrapper) {
          height: 34px;
          border-radius: 6px;
          box-shadow: 0 0 0 1px #dcdfe6 inset;
          transition: all 0.2s ease;

          &:hover {
            box-shadow: 0 0 0 1px #c0c4cc inset;
          }

          &.is-focus {
            box-shadow: 0 0 0 1px #409eff inset;
          }
        }

        :deep(.el-input__inner) {
          height: 34px;
          line-height: 34px;
          font-size: 13px;
          color: #303133;
          font-weight: 500;
        }

        :deep(.el-select__placeholder) {
          font-size: 13px;
          color: #a8abb2;
          font-weight: 500;
        }
      }
    }
  }

  .entity-tree-section {
    flex: 1;
    background: #ffffff;

    .tree-tabs {
      padding: 0;
      border-bottom: 1px solid #e8eaec;
      background: #ffffff;

      .tab-item {
        display: inline-block;
        padding: 14px 20px;
        font-size: 14px;
        color: #606266;
        font-weight: 500;
        cursor: pointer;
        position: relative;
        transition: all 0.3s ease;
        background: linear-gradient(135deg, #f8f9ff 0%, #f0f5ff 100%);
        border-radius: 8px 8px 0 0;
        margin-right: 2px;

        &:hover {
          color: #409eff;
          background: linear-gradient(135deg, #ecf5ff 0%, #e6f2ff 100%);
        }

        &.active {
          color: #409eff;
          background: linear-gradient(135deg, #e6f4ff 0%, #ddeeff 100%);
          box-shadow: 0 -2px 8px rgba(64, 158, 255, 0.1);

          &::after {
            content: "";
            position: absolute;
            bottom: -1px;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(90deg, #409eff 0%, #66b3ff 100%);
            border-radius: 3px 3px 0 0;
          }
        }
      }
    }

    .tree-container {
      padding: 0;

      .entity-tree {
        padding: 8px 12px;

        // 性能优化：启用GPU加速
        transform: translateZ(0);
        will-change: scroll-position;

        :deep(.el-tree-node__content) {
          height: 36px;
          padding: 0 12px;
          // 优化渲染性能
          contain: layout style;

          &:hover {
            background-color: #f5f7fa;
            // 使用transform代替background变化，减少重绘
            transform: translateX(0);
          }
        }

        :deep(.el-tree-node.is-current > .el-tree-node__content) {
          background-color: #f0f9f0;
          color: #52c41a;
        }

        // 优化滚动性能
        :deep(.el-scrollbar__view) {
          contain: layout style;
        }

        .tree-node {
          display: flex;
          align-items: center;
          width: 100%;
          // 性能优化
          contain: layout style;

          .folder-icon,
          .document-icon {
            font-size: 16px;
            margin-right: 8px;
            flex-shrink: 0;
            // 优化图标渲染
            will-change: auto;
          }

          .folder-icon {
            color: #409eff;
          }

          .document-icon {
            color: #909399;
          }

          .node-label {
            flex: 1;
            font-size: 13px;
            font-weight: 600;
            color: #303133;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            // 优化文本渲染
            contain: layout style;
          }

          .region-tag {
            font-size: 12px;
            color: #909399;
            background: #f4f4f5;
            padding: 2px 6px;
            border-radius: 10px;
            margin-left: 8px;
            flex-shrink: 0;
            // 优化标签渲染
            contain: layout style;
          }
        }
      }
    }
  }
}

/* 右侧内容区域样式 */
.right-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .page-title-bar {
    flex-shrink: 0;
    display: flex;
    align-items: baseline;
    gap: 12px;
    padding: 0 0 20px;

    .page-title {
      font-size: 20px;
      font-weight: 700;
      color: #18181b;
      letter-spacing: -0.02em;
      line-height: 1.2;
    }

    .page-subtitle {
      font-size: 14px;
      color: #71717a;
      font-weight: 400;
    }
  }

  .content-tabs {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 0;

    .custom-tabs {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;

      .tab-nav {
        flex-shrink: 0;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
        padding: 0 0 20px;

        @media (min-width: 900px) {
          grid-template-columns: repeat(4, 1fr);
        }

        .tab-item {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 16px;
          cursor: pointer;
          transition: all 0.2s ease;
          background: #FFFFFF;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          text-align: center;

          .tab-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            color: #18181b;
            transition: all 0.2s ease;
            .el-icon { font-size: 18px; }
          }

          .tab-label {
            font-size: 13px;
            color: #09090b;
            font-weight: 400;
            transition: all 0.2s ease;
            white-space: nowrap;
          }

          .tab-indicator { display: none; }

          &:hover {
            border-color: rgba(37,99,235,0.2);
            box-shadow: 0 2px 8px rgba(0,0,0,0.06);
          }

          &.active {
            border-color: rgba(37,99,235,0.3);
            background: rgba(37,99,235,0.05);
            box-shadow: 0 1px 3px rgba(0,0,0,0.04);
            .tab-icon { color: #2563eb; }
            .tab-label { color: #2563eb; }
          }
        }
      }

      .tab-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;

        .cur-bill-content,
        .settings-content,
        .integration-content,
        .rebate-content {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
      }
    }
  }
}

/* 账单内容样式 */
.bill-content {
  padding: 20px;

  .chart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding: 16px 20px;
    background: #ffffff;
    border: 1px solid #e8eaec;
    border-radius: 8px 8px 0 0;

    &.unified-header {
      border-bottom: none;
    }

    .header-left {
      display: flex;
      align-items: center;

      .chart-icon {
        color: #409eff;
        margin-right: 8px;
        font-size: 16px;
      }

      .chart-title {
        font-size: 16px;
        font-weight: 500;
        color: #303133;
        margin-right: 8px;
      }

      .info-icon {
        color: #909399;
        font-size: 14px;
        cursor: pointer;

        &:hover {
          color: #409eff;
        }
      }
    }

    .chart-actions {
      display: flex;
      align-items: center;

      .orange-button.el-button--warning {
        --el-button-bg-color: #ff7f50;
        --el-button-border-color: #ff7f50;
        --el-button-hover-bg-color: #ff9770;
        --el-button-hover-border-color: #ff9770;
        --el-button-active-bg-color: #e66f40;
        --el-button-active-border-color: #e66f40;
      }

      .chart-radio-group {
        :deep(.el-radio-button__inner) {
          font-size: 12px;
          padding: 6px 12px;
          border-radius: 4px;
          transition: all 0.3s ease;

          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
          }
        }
      }
    }
  }

  .chart-container {
    background: #ffffff;
    border: 1px solid #e8eaec;
    border-radius: 0 0 8px 8px;
    overflow: hidden;

    &.unified-card {
      border-top: none;
    }

    .chart-view {
      height: 400px;
      padding: 20px;
    }

    .chart-footer {
      padding: 16px 20px;
      background: #fafbfc;
      border-top: 1px solid #e8eaec;

      .footer-text {
        font-size: 14px;
        color: #909399;
        line-height: 1.5;
        padding-left: 8%;

        .asterisk {
          color: #f56c6c;
          font-weight: bold;
          margin-right: 4px;
        }
      }
    }
  }

  .stats-section {
    margin-top: 20px;

    .stats-row {
      display: flex;
      gap: 20px;

      .stats-card {
        flex: 1;
        background: #ffffff;
        border: 1px solid #e8eaec;
        border-radius: 8px;
        padding: 16px;

        .card-header-small {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;

          .header-left {
            display: flex;
            align-items: center;

            .card-icon {
              color: #409eff;
              margin-right: 8px;
              font-size: 16px;
            }

            .card-title {
              font-size: 14px;
              font-weight: 500;
              color: #303133;
              margin-right: 8px;
            }

            .card-date {
              font-size: 12px;
              color: #909399;
            }
          }

          .header-actions {
            .view-bill-btn {
              font-size: 12px;
              padding: 4px 12px;
              border-radius: 4px;
              transition: all 0.3s ease;

              &:hover {
                transform: translateY(-1px);
                box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
              }
            }
          }
        }

        .stats-cards {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;

          .stat-item {
            flex: 1;
            padding: 12px;
            border-radius: 6px;
            text-align: center;
            cursor: pointer;
            transition: all 0.3s ease;
            position: relative;

            &:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            }

            &.active::after {
              content: "";
              position: absolute;
              top: -2px;
              left: -2px;
              right: -2px;
              bottom: -2px;
              border: 2px solid;
              border-radius: 8px;
              pointer-events: none;
            }

            &.webdirect {
              border: 1px solid #1890ff;
              background: #f0f8ff;

              &.active::after {
                border-color: #1890ff;
              }

              .stat-value {
                color: #1890ff;
              }
            }

            &.package {
              border: 1px solid #909399;
              background: #f8f9fa;

              &.active::after {
                border-color: #909399;
              }

              .stat-value {
                color: #909399;
              }
            }

            .stat-label {
              font-size: 12px;
              color: #606266;
              margin-bottom: 4px;
            }

            .stat-value {
              font-size: 16px;
              font-weight: 600;
            }
          }
        }

        .chart-container-small {
          .pie-chart {
            height:350px;
          }
        }
      }

      .yearly-chart {
        flex: 1;
        background: #ffffff;
        border: 1px solid #e8eaec;
        border-radius: 8px;
        padding: 16px;

        .card-header-small {
          display: flex;
          align-items: center;
          margin-bottom: 16px;

          .card-icon {
            margin-right: 8px;
            font-size: 16px;
            color: #409eff;
          }

          .card-title {
            font-size: 14px;
            font-weight: 500;
            color: #303133;
            margin-right: 8px;
          }

          .card-date {
            font-size: 12px;
            color: #909399;
          }
        }

        .yearly-stats {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;

          .stat-item-yearly {
            flex: 1;
            padding: 8px 12px;
            border-radius: 4px;
            text-align: center;
            cursor: pointer;
            transition: all 0.3s ease;
            opacity: 0.5;

            &.active {
              opacity: 1;
            }

            &:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            }

            &.webdirect-yearly {
              border: 1px solid #1890ff;
              background: #f0f8ff;

              .stat-value {
                color: #1890ff;
              }
            }

            &.package-yearly {
              border: 1px solid #ff7f50;
              background: #fff5f0;

              .stat-value {
                color: #ff7f50;
              }
            }

            .stat-label {
              font-size: 11px;
              color: #606266;
              margin-bottom: 2px;
            }

            .stat-value {
              font-size: 14px;
              font-weight: 600;
            }
          }
        }

        .line-chart {
          height: 310px;
        }
      }
    }
  }
}

/* 账单设置样式 */
.settings-content {
  padding: 20px;

  .config-download-section {
    background: #ffffff;
    border: 1px solid #e8eaec;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    align-items: flex-end;

    .config-options {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      flex: 1;
      min-width: 0;

      .option-item {
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: 140px;

        .option-label {
          font-size: 13px;
          color: #303133;
          font-weight: 500;
          white-space: nowrap;
        }

        .option-select {
          width: 160px;

          :deep(.el-input__wrapper) {
            height: 32px;
            border-radius: 4px;
          }

          :deep(.el-input__inner) {
            font-size: 13px;
          }
        }
      }
    }

    .download-buttons {
      display: flex;
      gap: 12px;
      flex-shrink: 0;

      .download-btn {
        padding: 8px 20px;
        font-size: 14px;
        font-weight: 500;
        border-radius: 4px;
        white-space: nowrap;

        .el-icon {
          margin-right: 6px;
        }
      }
    }

    /* 响应式设计 */
    @media (max-width: 1200px) {
      flex-direction: column;
      align-items: stretch;

      .config-options {
        justify-content: flex-start;
      }

      .download-buttons {
        justify-content: flex-end;
      }
    }
  }

  .settings-card {
    background: #ffffff;
    border: 1px solid #e8eaec;
    border-radius: 8px;
    padding: 24px;
    margin-bottom: 16px; /* 减少卡片间距从20px到16px */

    &:last-child {
      margin-bottom: 0;
    }

    .settings-header {
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 1px solid #e8eaec;

      .settings-title {
        font-size: 18px;
        font-weight: 600;
        color: #303133;
        margin: 0 0 8px 0;
      }

      .settings-desc {
        font-size: 14px;
        color: #909399;
        margin: 0;
      }
    }

    .settings-form {
      .form-group {
        margin-bottom: 16px; /* 从24px减少到16px */
        display: flex;
        align-items: center;
        gap: 12px;

        &:last-child {
          margin-bottom: 0;
        }

        .form-label {
          font-size: 14px;
          font-weight: 500;
          color: #303133;
          white-space: nowrap;
          min-width: 80px;
        }

        .form-select {
          width: 200px;
        }

        .form-input {
          width: 300px;

          :deep(.el-textarea__inner) {
            border-radius: 4px;
            font-size: 13px;
            line-height: 1.4;
          }

          :deep(.el-input__count) {
            font-size: 12px;
            color: #909399;
          }
        }

        /* 对于有下拉框的表单组，使用flex布局 */
        &:has(.form-select) {
          .form-label {
            margin-bottom: 0;
          }
        }

        /* 对于单选框组，保持垂直布局 */
        &:has(.el-radio-group) {
          flex-direction: column;
          align-items: flex-start;

          .form-label {
            margin-bottom: 8px;
          }
        }

        /* 对于有上传组件的表单组，保持垂直布局 */
        &:has(.el-upload) {
          flex-direction: column;
          align-items: flex-start;

          .form-label {
            margin-bottom: 8px;
          }
        }

        /* 上传区域样式 */
        .logo-upload {
          width: 100%;

          :deep(.el-upload) {
            width: 100%;
          }

          :deep(.el-upload-dragger) {
            width: 100%;
            height: 120px;
            border: 2px dashed #d9d9d9;
            border-radius: 6px;
            cursor: pointer;
            position: relative;
            overflow: hidden;
            transition: border-color 0.3s;

            &:hover {
              border-color: #409eff;
            }
          }
        }

        /* 上传触发框样式 */
        .upload-trigger-box {
          width: 100%;
          height: 120px;
          border: 2px dashed #d9d9d9;
          border-radius: 6px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          background: #fafbfc;

          &:hover {
            border-color: #409eff;
            background: #f0f8ff;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);

            .upload-icon {
              color: #409eff;
              transform: scale(1.1);
            }

            .upload-text {
              color: #409eff;
            }
          }

          .upload-icon {
            font-size: 36px;
            color: #909399;
            margin-bottom: 8px;
            transition: all 0.3s ease;
          }

          .upload-text {
            font-size: 14px;
            font-weight: 500;
            color: #303133;
            margin-bottom: 4px;
            transition: all 0.3s ease;
          }

          .upload-tip {
            font-size: 12px;
            color: #909399;
          }
        }

        /* 上传禁用提示样式 */
        .upload-disabled-tip {
          width: 100%;
          height: 120px;
          border: 2px dashed #e4e7ed;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #909399;
          font-size: 14px;
          background-color: #fafafa;
        }

        :deep(.el-radio-group) {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;

          .el-radio {
            margin-right: 0;
          }
        }

        :deep(.el-checkbox-group) {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;

          .el-checkbox {
            margin-right: 0;
          }
        }
      }

      .form-actions {
        margin-top: 32px;
        padding-top: 16px;
        border-top: 1px solid #e8eaec;
        display: flex;
        gap: 12px;

        .save-btn {
          padding: 8px 24px;
        }

        .reset-btn {
          padding: 8px 24px;
        }
      }
    }
  }


}

/* 整合表下载样式 */
.integration-content {
  padding: 20px;

  .integration-search-section {
    background: #ffffff;
    border: 0;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
    padding: 20px 24px;
    margin-bottom: 16px;

    .search-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 16px 20px;
      align-items: end;

      .search-item {
        display: flex;
        flex-direction: column;
        gap: 6px;

        .search-label {
          font-size: 11px;
          color: #6b7280;
          font-weight: 500;
          white-space: nowrap;
          line-height: 1;
        }

        .search-input {
          width: 100%;

          :deep(.el-input__wrapper) {
            height: 34px;
            border-radius: 8px;
            box-shadow: none;
            background: #f3f4f6;
            border: none;
            &:hover { background: #e9eaec; }
            &.is-focus { box-shadow: 0 0 0 2px var(--primary, #1D6FE8) inset; background: #fff; }
          }

          :deep(.el-input__inner) {
            font-size: 13px;
          }
        }
      }

      .search-actions {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
        grid-column: 1 / -1;

        .search-btn {
          height: 34px;
          padding: 0 20px;
          font-size: 13px;
          font-weight: 500;
          background: #1a1a2e;
          border-color: #1a1a2e;
          color: #fff;
          border-radius: 8px;
          &:hover { background: #374151; border-color: #374151; }
        }
        
        .regen-btn {
          height: 34px;
          padding: 0 16px;
          font-size: 13px;
          background: #fff;
          border: 1px solid #e5e7eb;
          color: #1a1a2e;
          border-radius: 8px;
          &:hover { background: #f3f4f6; border-color: #9ca3af; }
        }
      }
    }
  }

  .integration-table-section {
    background: #ffffff;
    border: 1px solid #e8eaec;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 20px;

    .table-toolbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 20px;
      border-bottom: 1px solid #e8eaec;
      background: #fafbfc;
      flex-wrap: wrap;
      gap: 8px;

      .toolbar-left {
        display: flex;
        align-items: center;
        gap: 12px;

        .table-title {
          font-size: 16px;
          font-weight: 500;
          color: #303133;
        }

        .table-count {
          font-size: 13px;
          color: #909399;
        }
      }

      .toolbar-right {
        display: flex;
        gap: 8px;
        align-items: center;
        flex-wrap: wrap;
        justify-content: flex-end;
        
        .format-label {
          font-size: 13px;
          color: #606266;
          font-weight: 500;
        }
        
        .el-select {
          :deep(.el-input__wrapper) {
            height: 28px;
          }
          :deep(.el-input__inner) {
            font-size: 13px;
          }
        }
      }
    }

    .table-container {
      position: relative;
      border-bottom: 1px solid #e8eaec;

      :deep(.el-table) {
        .el-table__header-wrapper {
          .el-table__header {
            th {
              background: #f8f9fa;
              color: #303133;
              font-weight: 600;
              font-size: 13px;
              white-space: nowrap;
            }
          }
        }

        .el-table__body-wrapper {
          .el-table__body {
            td {
              font-size: 13px;
              color: #606266;
              white-space: nowrap;
            }
          }
        }
      }
      
    }

    .table-pagination {
      padding: 16px 20px;
      border-top: 1px solid #e8eaec;
      background: #fafbfc;
      display: flex;
      justify-content: flex-end;
    }
  }
}

/* 列设置弹窗样式 */
.column-settings {
  .settings-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e8eaec;

    .settings-actions {
      display: flex;
      gap: 8px;
    }
  }

  .column-list {
    max-height: 400px;
    overflow-y: auto;

    .column-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      border: 1px solid #e8eaec;
      border-radius: 4px;
      margin-bottom: 8px;
      cursor: move;
      transition: all 0.3s ease;

      &:hover {
        background: #f5f7fa;
        border-color: #c0c4cc;
      }

      .drag-handle {
        color: #c0c4cc;
        cursor: move;

        &:hover {
          color: #409eff;
        }
      }
    }
  }
}

/* Invoice上传对话框样式 */
.invoice-upload-area {
  position: relative;
  transition: all 0.3s ease;
  
  &.is-dragging {
    .custom-upload-box {
      border-color: #409eff;
      background-color: #ecf5ff;
    }
  }
  
  .custom-upload-box {
    width: 100%;
    height: 200px;
    border: 2px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    background: #fafafa;
    
    &:hover {
      border-color: #409eff;
      background-color: #ecf5ff;
    }
    
    .el-icon--upload {
      font-size: 67px;
      color: #c0c4cc;
      margin-bottom: 16px;
    }
    
    .el-upload__text {
      color: #606266;
      font-size: 14px;
      text-align: center;
      
      em {
        color: #409eff;
        font-style: normal;
      }
    }
    
    .el-upload__tip {
      font-size: 12px;
      color: #909399;
      margin-top: 7px;
      text-align: center;
    }
  }
}

.file-list {
  margin-top: 15px;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 10px;
  
  .file-count {
    font-size: 13px;
    color: #909399;
    margin-bottom: 10px;
    padding: 0 4px;
    font-weight: 500;
  }
  
  .file-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    margin-bottom: 8px;
    background-color: #f5f7fa;
    border-radius: 4px;
    transition: all 0.3s ease;
    gap: 12px;
    
    &:hover {
      background-color: #ecf5ff;
      
      .remove-icon {
        opacity: 1;
      }
    }
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .file-path {
      flex: 1;
      font-size: 13px;
      color: #606266;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-family: 'Consolas', 'Monaco', monospace;
    }
    
    .remove-icon {
      font-size: 16px;
      color: #f56c6c;
      cursor: pointer;
      opacity: 0.6;
      transition: opacity 0.3s ease;
      flex-shrink: 0;
      
      &:hover {
        opacity: 1;
      }
    }
  }
}
</style>

// 全局样式：统一弹窗文字为绿色
<style lang="scss">
.el-message {
  .el-message__content {
    color: #67c23a !important; /* Element UI 绿色 */
  }
}
</style>

